﻿@file:OptIn(ExperimentalMaterial3Api::class, ExperimentalFoundationApi::class)

package com.example.monetix.ui.screens

import androidx.compose.animation.*
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.ExperimentalFoundationApi
import androidx.compose.foundation.background
import androidx.compose.foundation.basicMarquee
import androidx.compose.foundation.clickable
import androidx.compose.foundation.combinedClickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.Visibility
import androidx.compose.material.icons.outlined.VisibilityOff
import androidx.compose.material.icons.outlined.CheckCircle
import androidx.compose.material.icons.rounded.AccountBalance
import androidx.compose.material.icons.rounded.AutoFixHigh
import androidx.compose.material.icons.rounded.CheckCircle
import androidx.compose.material.icons.rounded.LocalFireDepartment
import androidx.compose.material.icons.rounded.RadioButtonUnchecked
import androidx.compose.material3.*
import com.example.monetix.ui.components.MonetixTextField as OutlinedTextField
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.blur
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.spring
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.tween
import kotlinx.coroutines.delay
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import androidx.compose.ui.hapticfeedback.HapticFeedbackType
import androidx.compose.ui.platform.LocalHapticFeedback
import androidx.compose.ui.zIndex

import com.example.monetix.db.RecurringExpenseEntity
import com.example.monetix.db.TransactionEntity
import com.example.monetix.db.AccountEntity
import com.example.monetix.db.CategoryEntity
import com.example.monetix.stripSystemMarker
import com.example.monetix.ui.components.ModernMoneyText
import com.example.monetix.ui.components.TxLogo
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn
import kotlinx.datetime.LocalDate
import kotlinx.datetime.toLocalDateTime
import kotlin.random.Random

val RecurringExpenseEntity.isCartella: Boolean
    get() = this.id.startsWith("cart_") || this.id.startsWith("tax_") || this.notes?.contains("[CART:", ignoreCase = true) == true || this.notes?.contains("[TAX:", ignoreCase = true) == true

@OptIn(ExperimentalMaterial3Api::class, ExperimentalFoundationApi::class)
@Composable
fun RecurringScreen(
    recurringItems: List<RecurringExpenseEntity>,
    transactions: List<TransactionEntity>,
    accounts: List<AccountEntity>,
    categories: List<CategoryEntity> = emptyList(),
    hideBalances: Boolean,
    onToggleHideBalances: () -> Unit,
    onAddClick: () -> Unit,
    onEditClick: (RecurringExpenseEntity) -> Unit,
    onDeleteClick: (RecurringExpenseEntity) -> Unit,
    onToggleEnable: (RecurringExpenseEntity, Boolean) -> Unit,
    onUpdate: (RecurringExpenseEntity) -> Unit,
    onGenerateRecurring: () -> Unit,
    isGenerating: Boolean,
    onLiquidateCycle: (RecurringExpenseEntity, Long, Long, Long, Boolean) -> Unit,
    onPayRecurringEarly: (RecurringExpenseEntity) -> Unit,
    onPopupStateChanged: (Boolean) -> Unit = {},
    onSaveTransaction: ((TransactionEntity) -> Unit)? = null,
    payPeriodEnd: LocalDate? = null
) {
    val listState = rememberLazyListState()

    val isCollapsed by remember { derivedStateOf { listState.firstVisibleItemIndex > 0 || listState.firstVisibleItemScrollOffset > 50 } }
    val haptic = LocalHapticFeedback.current

    var deleteTarget by remember { mutableStateOf<RecurringExpenseEntity?>(null) }
    var estinguiTarget by remember { mutableStateOf<RecurringExpenseEntity?>(null) }
    var toggleEnableTarget by remember { mutableStateOf<RecurringExpenseEntity?>(null) }
    var detailsRecurring by remember { mutableStateOf<RecurringExpenseEntity?>(null) }
    var activeDetailsRecurring by remember { mutableStateOf<RecurringExpenseEntity?>(null) }
    var isHeaderExpanded by rememberSaveable { mutableStateOf(false) }
    var cycleToManage by remember { mutableStateOf<RecurringExpenseEntity?>(null) }
    var showGenerationFeedback by remember { mutableStateOf(false) }

    var liquidateTarget by remember { mutableStateOf<RecurringExpenseEntity?>(null) }
    var liquidateAmount by remember { mutableStateOf(0L) }
    var liquidateMonths by remember { mutableStateOf(0L) }

    var recurringToPay by remember { mutableStateOf<RecurringExpenseEntity?>(null) }

    var recurringToEdit by remember { mutableStateOf<RecurringExpenseEntity?>(null) }
    var showRecurringDialog by remember { mutableStateOf(false) }
    var showCartellaWizard by remember { mutableStateOf(false) }
    var showTassaWizard by remember { mutableStateOf(false) }
    var activeAccountId by remember { mutableStateOf<String?>(null) }

    val (monthlyExpenseCents, monthlyTransferCents, monthlyIncomeCents) = remember(recurringItems) {
        val activeItems = recurringItems.filter { !it.isDeleted && it.isEnabled }

        val expenses = activeItems.filter { it.type != "INCOME" }.sumOf { it.amountCents }
        val transfers = activeItems.filter { it.type == "TRANSFER" || it.toAccountId != null }.sumOf { it.amountCents }
        val incomes = activeItems.filter { it.type == "INCOME" }.sumOf { it.amountCents }
        Triple(expenses, transfers, incomes)
    }

    val yearlyExpenseCents = monthlyExpenseCents * 12
    val activeSubscriptionsCount = recurringItems.count { !it.isDeleted && it.isEnabled && it.type != "INCOME" }

    val brandPrimary = Color(0xFF60A5FA)
    val incomeColor = Color(0xFF10B981)

    val isRecurringPopupOpen = deleteTarget != null || estinguiTarget != null || toggleEnableTarget != null ||
            detailsRecurring != null || activeDetailsRecurring != null || cycleToManage != null ||
            liquidateTarget != null || recurringToPay != null || showCartellaWizard || showTassaWizard || showGenerationFeedback

    LaunchedEffect(isRecurringPopupOpen) {
        onPopupStateChanged(isRecurringPopupOpen)
    }

    val recurringBlur by animateDpAsState(
        targetValue = if (isRecurringPopupOpen) 12.dp else 0.dp,
        animationSpec = tween(durationMillis = 300),
        label = "recurringBlur"
    )

    Scaffold(
        containerColor = Color.Transparent
    ) { paddingValues ->
        val hasCustomBg = com.example.monetix.LocalHasCustomBackground.current

        Box(
            modifier = Modifier
                .fillMaxSize()
                .then(
                    if (hasCustomBg) Modifier.background(Color.Transparent)
                    else Modifier.background(
                        Brush.verticalGradient(
                            colors = listOf(
                                Color(0xFF064E3B).copy(alpha = 0.6f),
                                Color(0xFF1E1B4B),
                                Color(0xFF0F172A)
                            )
                        )
                    )
                )
                .blur(recurringBlur)
                .padding(paddingValues)
        ) {
            Column(
                modifier = Modifier.fillMaxSize()
            ) {
                Surface(
                    modifier = Modifier.fillMaxWidth().zIndex(1f),
                    color = Color.Transparent,
                    shadowElevation = 0.dp
                ) {
                Column(modifier = Modifier.statusBarsPadding().padding(start = 16.dp, end = 16.dp, top = 20.dp, bottom = 12.dp)) {

                    Row(modifier = Modifier.fillMaxWidth().padding(bottom = 16.dp), verticalAlignment = Alignment.CenterVertically) {
                        Text("Spese Fisse", style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Black), color = Color.White)
                        Spacer(Modifier.weight(1f))
                        IconButton(
                            onClick = onToggleHideBalances,
                            modifier = Modifier.size(36.dp)
                        ) {
                            Icon(if (hideBalances) Icons.Outlined.VisibilityOff else Icons.Outlined.Visibility, null, tint = Color.White)
                        }
                    }

                    Surface(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clickable(
                                interactionSource = remember { MutableInteractionSource() },
                                indication = null,
                                onClick = {
                                    haptic.performHapticFeedback(HapticFeedbackType.TextHandleMove)
                                    isHeaderExpanded = !isHeaderExpanded
                                }
                            ),
                        shape = RoundedCornerShape(24.dp),
                        color = Color.White.copy(alpha = 0.08f),
                        border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f)),
                        shadowElevation = 0.dp
                    ) {
                        Column(modifier = Modifier.animateContentSize()) {
                            if (isCollapsed) {
                                Row(
                                    modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 12.dp),
                                    verticalAlignment = Alignment.CenterVertically,
                                    horizontalArrangement = Arrangement.SpaceBetween
                                ) {
                                    Column(modifier = Modifier.weight(1f)) {
                                        Text("Uscite Fisse Mensili", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0))
                                        ModernMoneyText(cents = monthlyExpenseCents, hideBalances = hideBalances, color = MaterialTheme.colorScheme.error, integerFontSize = 22.sp, decimalFontSize = 14.sp, symbolFontSize = 14.sp)
                                    }
                                    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                                        IconButton(onClick = onAddClick, modifier = Modifier.background(brandPrimary.copy(alpha=0.15f), CircleShape).size(36.dp)) {
                                            Icon(Icons.Default.Add, null, tint = brandPrimary, modifier = Modifier.size(20.dp))
                                        }
                                    }
                                }
                            } else {
                                Column(modifier = Modifier.padding(20.dp)) {
                                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                                        Text(if (isHeaderExpanded) "CHIUDI OPZIONI" else "BILANCIO FISSO MENSILE", style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Normal), color = Color(0xFFA0A0A0))
                                        Icon(if (isHeaderExpanded) Icons.Default.ExpandLess else Icons.Default.ExpandMore, null, tint = Color(0xFFA0A0A0))
                                    }

                                    Spacer(Modifier.height(16.dp))

                                    Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Bottom) {
                                        Column {
                                            Text("Entrate", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0))
                                            ModernMoneyText(cents = monthlyIncomeCents, hideBalances = hideBalances, sign = "+", color = incomeColor, integerFontSize = 24.sp, decimalFontSize = 14.sp, symbolFontSize = 14.sp)
                                        }
                                        Column(horizontalAlignment = Alignment.End) {
                                            Text("Uscite", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0))
                                            ModernMoneyText(cents = monthlyExpenseCents, hideBalances = hideBalances, sign = "-", color = MaterialTheme.colorScheme.error, integerFontSize = 24.sp, decimalFontSize = 14.sp, symbolFontSize = 14.sp)
                                        }
                                    }

                                    AnimatedVisibility(visible = !isHeaderExpanded) {
                                        Column {
                                            Spacer(Modifier.height(16.dp))
                                            HorizontalDivider(color = Color.White.copy(alpha = 0.08f))
                                            Spacer(Modifier.height(12.dp))

                                            Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Bottom) {
                                                Column {
                                                    Row(verticalAlignment = Alignment.CenterVertically) {
                                                        Icon(Icons.Rounded.LocalFireDepartment, null, tint = MaterialTheme.colorScheme.error, modifier = Modifier.size(14.dp))
                                                        Spacer(Modifier.width(4.dp))
                                                        Text("Costo Annuo", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0))
                                                    }
                                                    ModernMoneyText(cents = yearlyExpenseCents, hideBalances = hideBalances, color = MaterialTheme.colorScheme.error, integerFontSize = 16.sp, decimalFontSize = 12.sp, symbolFontSize = 12.sp)
                                                }
                                                Column(horizontalAlignment = Alignment.End) {
                                                    Text("$activeSubscriptionsCount Attivi", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0))
                                                    if (monthlyTransferCents > 0) {
                                                        Row(verticalAlignment = Alignment.Bottom) {
                                                            Text("di cui ", style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0), modifier = Modifier.padding(bottom = 1.dp))
                                                            ModernMoneyText(cents = monthlyTransferCents, hideBalances = hideBalances, sign = "", color = brandPrimary, integerFontSize = 12.sp, decimalFontSize = 10.sp, symbolFontSize = 10.sp)
                                                            Text(" in giroconti", style = MaterialTheme.typography.bodySmall.copy(fontWeight = FontWeight.Bold), color = brandPrimary, modifier = Modifier.padding(bottom = 1.dp, start = 4.dp))
                                                        }
                                                    } else {
                                                        Text("Impatto uscite fisse", style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0))
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    AnimatedVisibility(visible = isHeaderExpanded && !isCollapsed, enter = expandVertically(), exit = shrinkVertically()) {
                        Column(modifier = Modifier.padding(top = 16.dp)) {
                            Row(
                                modifier = Modifier.fillMaxWidth().padding(horizontal = 20.dp),
                                horizontalArrangement = Arrangement.spacedBy(12.dp)
                            ) {
                                Surface(
                                    onClick = onAddClick,
                                    shape = RoundedCornerShape(18.dp),
                                    color = brandPrimary.copy(alpha = 0.15f),
                                    modifier = Modifier.weight(1f).height(68.dp)
                                ) {
                                    Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center) {
                                        Icon(Icons.Default.Add, contentDescription = "Nuova Voce", tint = brandPrimary, modifier = Modifier.size(24.dp))
                                        Spacer(Modifier.height(4.dp))
                                        Text("Nuovo", style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold), color = brandPrimary)
                                    }
                                }

                                Surface(
                                    onClick = { recurringToEdit = null; activeAccountId = null; showCartellaWizard = true },
                                    shape = RoundedCornerShape(18.dp),
                                    color = MaterialTheme.colorScheme.error.copy(alpha = 0.15f),
                                    modifier = Modifier.weight(1f).height(68.dp)
                                ) {
                                    Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center) {
                                        Icon(Icons.Rounded.AccountBalance, contentDescription = "Cartella Rate", tint = MaterialTheme.colorScheme.error, modifier = Modifier.size(24.dp))
                                        Spacer(Modifier.height(4.dp))
                                        Text("Cartelle", style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold), color = MaterialTheme.colorScheme.error)
                                    }
                                }

                                Surface(
                                    onClick = { recurringToEdit = null; activeAccountId = "NEW_TAX_MODE"; showTassaWizard = true },
                                    shape = RoundedCornerShape(18.dp),
                                    color = Color(0xFFF59E0B).copy(alpha = 0.15f),
                                    modifier = Modifier.weight(1f).height(68.dp)
                                ) {
                                    Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center) {
                                        Icon(Icons.Rounded.AccountBalance, contentDescription = "Tasse Comunali", tint = Color(0xFFF59E0B), modifier = Modifier.size(24.dp))
                                        Spacer(Modifier.height(4.dp))
                                        Text("Tasse", style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold), color = Color(0xFFF59E0B))
                                    }
                                }

                                Surface(
                                    onClick = { onGenerateRecurring(); showGenerationFeedback = true },
                                    shape = RoundedCornerShape(18.dp),
                                    color = Color(0xFF10B981).copy(alpha = 0.15f),
                                    modifier = Modifier.weight(1f).height(68.dp)
                                ) {
                                    Column(horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.Center) {
                                        if (isGenerating) {
                                            CircularProgressIndicator(modifier = Modifier.size(24.dp), color = Color(0xFF10B981), strokeWidth = 2.dp)
                                        } else {
                                            Icon(Icons.Rounded.AutoFixHigh, contentDescription = "Genera", tint = Color(0xFF10B981), modifier = Modifier.size(24.dp))
                                        }
                                        Spacer(Modifier.height(4.dp))
                                        Text("Genera", style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold), color = Color(0xFF10B981))
                                    }
                                }
                            }
                            Spacer(Modifier.height(16.dp))
                            HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.1f))
                            Spacer(Modifier.height(8.dp))
                        }
                    }
                }
            }

            val hasTaxBills = remember(recurringItems) { recurringItems.any { !it.isDeleted && it.isCartella } }

            LazyColumn(
                state = listState,
                modifier = Modifier.weight(1f),
                contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp)
            ) {

                if (hasTaxBills) {
                    item {
                        TaxBillsTrackerCard(
                            recurringItems = recurringItems,
                            pastTransactions = transactions,
                            onEditCartella = { cartella ->
                                recurringToEdit = cartella
                                if (cartella.notes?.contains("[TAX:", ignoreCase = true) == true) {
                                    showTassaWizard = true
                                } else {
                                    showCartellaWizard = true
                                }
                            },
                            onDeleteCartella = { cartella ->
                                deleteTarget = cartella
                            },
                            onEstinguiCartella = { cartella ->
                                estinguiTarget = cartella
                            },
                            onOpenDetails = { cartella ->
                                detailsRecurring = cartella
                            },
                            hideBalances = hideBalances
                        )
                        Spacer(Modifier.height(16.dp))
                    }
                }

                if (recurringItems.none { !it.isDeleted }) {
                    item {
                        Box(Modifier.fillMaxWidth().height(200.dp), contentAlignment = Alignment.Center) {
                            Text("NESSUNA AUTOMAZIONE", style = MaterialTheme.typography.labelMedium, color = Color(0xFFA0A0A0))
                        }
                    }
                } else {
                    items(recurringItems.filter { !it.isDeleted }, key = { it.id }) { r ->

                        val isIncome = r.type == "INCOME"
                        val isTransfer = r.type == "TRANSFER" || r.toAccountId != null

                        val acc = accounts.firstOrNull { it.id == r.accountId }
                        val toAcc = accounts.firstOrNull { it.id == r.toAccountId }

                        val displayEmoji = if (isTransfer) toAcc?.logoEmoji ?: r.logoEmoji else r.logoEmoji
                        val displayUri = if (isTransfer) toAcc?.logoUri ?: r.logoUri else r.logoUri
                        val fallbackName = if (isTransfer) toAcc?.name ?: r.name ?: "MOVIMENTO" else r.name ?: "MOVIMENTO"
                        val catName = categories.firstOrNull { it.id == r.categoryId }?.name ?: "ALTRO"

                        val subtitleText = if (isTransfer) {
                            val fromName = stripSystemMarker(acc?.name) ?: "CONTO"
                            val toName = stripSystemMarker(toAcc?.name) ?: "CONTO"
                            "$fromName -> $toName • GG ${r.dayOfMonth}".uppercase()
                        } else {
                            "${stripSystemMarker(acc?.name) ?: "CONTO"} • GG ${r.dayOfMonth} • $catName".uppercase()
                        }

                        val target = r.cycleTarget?.toLong() ?: 0L
                        val isSinkingFund = target > 0L && !isIncome && !r.isCartella

                        var sinkingFundText: String? = null
                        var isCycleComplete = false
                        var currentAccumulated = 0L

                        if (isSinkingFund) {
                            val count = transactions.count { tx ->
                                !tx.isDeleted &&
                                        (tx.description?.contains("[rec:${r.id}") == true || tx.merchantKey?.contains("[rec:${r.id}") == true)
                            }.toLong()

                            val currentStep = if (count == 0L) 0L else if (count % target == 0L) target else count % target
                            currentAccumulated = currentStep * r.amountCents

                            isCycleComplete = (currentStep >= target && target > 0L)
                            sinkingFundText = "Ciclo $currentStep/$target • Accumulati: "
                        }

                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clickable { detailsRecurring = r }
                                .padding(vertical = 4.dp)
                        ) {
                            Row(modifier = Modifier.padding(vertical = 12.dp, horizontal = 4.dp), verticalAlignment = Alignment.CenterVertically) {
                                TxLogo(displayEmoji, displayUri, fallbackName, modifier = Modifier.size(40.dp))
                                Spacer(Modifier.width(16.dp))
                                Column(Modifier.weight(1f)) {
                                    Text(
                                        text = (r.name ?: "Automazione").uppercase(),
                                        style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.SemiBold),
                                        color = if (r.isEnabled) Color.White else Color(0xFFA0A0A0),
                                        maxLines = 1, overflow = TextOverflow.Ellipsis
                                    )
                                    Text(text = subtitleText, style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0), maxLines = 1, modifier = Modifier.basicMarquee())

                                    if (sinkingFundText != null) {
                                        Spacer(Modifier.height(4.dp))
                                        Row(verticalAlignment = Alignment.Bottom) {
                                            Icon(imageVector = if (isCycleComplete) Icons.Default.CheckCircle else Icons.Default.Sync, contentDescription = null, tint = if (isCycleComplete) Color(0xFF10B981) else brandPrimary, modifier = Modifier.size(14.dp).padding(bottom = 2.dp))
                                            Spacer(Modifier.width(4.dp))
                                            Text(text = sinkingFundText, style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Bold), color = if (isCycleComplete) Color(0xFF10B981) else brandPrimary, modifier = Modifier.padding(bottom = 1.dp))
                                            ModernMoneyText(cents = currentAccumulated, hideBalances = hideBalances, color = if (isCycleComplete) Color(0xFF10B981) else brandPrimary, integerFontSize = 11.sp, decimalFontSize = 9.sp, symbolFontSize = 9.sp)
                                        }
                                    }
                                }

                                val colorMain = if (r.isEnabled) {
                                    if (isIncome) incomeColor else if (isTransfer) brandPrimary else Color.White
                                } else Color(0xFFA0A0A0)

                                ModernMoneyText(
                                    cents = r.amountCents,
                                    hideBalances = hideBalances,
                                    sign = if (isIncome) "+" else "",
                                    color = colorMain,
                                    integerFontSize = 16.sp, decimalFontSize = 12.sp, symbolFontSize = 12.sp,
                                    modifier = Modifier.padding(end = 8.dp)
                                )
                            }
                            HorizontalDivider(color = Color.White.copy(alpha = 0.08f))
                        }
                    }
                    item { Spacer(Modifier.height(120.dp)) }
                }
            }
        }
    }
}

    if (showCartellaWizard) {
        val selectedAccId = if (activeAccountId != null && activeAccountId != "NEW_TAX_MODE") activeAccountId else null
        val realAccountId = selectedAccId
            ?: accounts.firstOrNull { !it.isDeleted && !it.isArchived && !it.id.startsWith("sys_") }?.id
            ?: accounts.firstOrNull { !it.isDeleted }?.id
            ?: ""

        CartellaWizardDialog(
            activeAccountId = realAccountId,
            accounts = accounts,
            onDismiss = { showCartellaWizard = false },
            onSaveRecurring = { newRec ->
                onUpdate(newRec)
                showCartellaWizard = false
            },
            onSaveTransaction = onSaveTransaction,
            editingEntity = recurringToEdit,
            payPeriodEnd = payPeriodEnd
        )
    }

    if (showTassaWizard) {
        val selectedAccId = if (activeAccountId != null && activeAccountId != "NEW_TAX_MODE") activeAccountId else null
        val realAccountId = selectedAccId
            ?: accounts.firstOrNull { !it.isDeleted && !it.isArchived && !it.id.startsWith("sys_") }?.id
            ?: accounts.firstOrNull { !it.isDeleted }?.id
            ?: ""

        TassaWizardDialog(
            activeAccountId = realAccountId,
            accounts = accounts,
            onDismiss = { showTassaWizard = false },
            onSaveRecurring = { newRec ->
                onUpdate(newRec)
                showTassaWizard = false
            },
            onSaveTransaction = onSaveTransaction,
            editingEntity = recurringToEdit,
            payPeriodEnd = payPeriodEnd
        )
    }

    if (showRecurringDialog) {
        // Gestito dal dialog dedicato.
    }

    // DIALOGO ESTINZIONE
    if (estinguiTarget != null) {
        AlertDialog(
            onDismissRequest = { estinguiTarget = null },
            shape = RoundedCornerShape(28.dp),
            containerColor = Color.White.copy(alpha = 0.12f),
            title = {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Outlined.CheckCircle, null, tint = Color(0xFF10B981), modifier = Modifier.size(24.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Estingui in Anticipo", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold), color = Color(0xFF10B981))
                }
            },
            text = { Text("Vuoi chiudere questo piano in anticipo? Verrà archiviato tra i 'Piani Saldati' e non genererà più nuove rate.\n\nNota: Ricorda di registrare manualmente la spesa dell'eventuale maxi-rata di saldo nella Home.", style = MaterialTheme.typography.bodyMedium, color = Color(0xFFA0A0A0)) },
            confirmButton = {
                Button(
                    onClick = {
                        val updated = estinguiTarget!!.copy(
                            isEnabled = false,
                            notes = (estinguiTarget!!.notes ?: "") + " [ARCHIVED:true]",
                            updatedAtEpochMs = Clock.System.now().toEpochMilliseconds()
                        )
                        onUpdate(updated)
                        estinguiTarget = null
                    },
                    shape = RoundedCornerShape(14.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF10B981), contentColor = Color.White)
                ) {
                    Text("ESTINGUI ORA", fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = {
                TextButton(onClick = { estinguiTarget = null }) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
            }
        )
    }

    if (deleteTarget != null) {
        AlertDialog(
            onDismissRequest = { deleteTarget = null },
            shape = RoundedCornerShape(28.dp),
            containerColor = Color.White.copy(alpha = 0.12f),
            title = { Text("Elimina Automazione", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold), color = MaterialTheme.colorScheme.error) },
            text = { Text("Vuoi eliminare questa automazione o cartella? Scomparirà definitivamente.", style = MaterialTheme.typography.bodyMedium, color = Color(0xFFA0A0A0)) },
            confirmButton = {
                Button(
                    onClick = { onDeleteClick(deleteTarget!!); deleteTarget = null },
                    shape = RoundedCornerShape(14.dp),
                    colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error, contentColor = Color.White)
                ) {
                    Text("ELIMINA", fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = { TextButton(onClick = { deleteTarget = null }) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) } }
        )
    }

    if (toggleEnableTarget != null) {
        val targetItem = toggleEnableTarget!!
        val isSuspending = targetItem.isEnabled
        val titleText = if (isSuspending) "Sospendi Automazione" else "Riattiva Automazione"
        val bodyText = if (isSuspending) {
            "Sei sicuro di voler sospendere l'automazione '${targetItem.name}'?\nNon verranno generati nuovi addebiti automatizzati finché non la riattiverai."
        } else {
            "Sei sicuro di voler riattivare l'automazione '${targetItem.name}'?\nTornerà attiva e riprenderà a generare le scadenze previste."
        }

        AlertDialog(
            onDismissRequest = { toggleEnableTarget = null },
            shape = RoundedCornerShape(28.dp),
            containerColor = Color.White.copy(alpha = 0.12f),
            title = {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(
                        if (isSuspending) Icons.Outlined.VisibilityOff else Icons.Default.CheckCircle,
                        contentDescription = null,
                        tint = if (isSuspending) MaterialTheme.colorScheme.error else Color(0xFF10B981),
                        modifier = Modifier.size(24.dp)
                    )
                    Spacer(Modifier.width(8.dp))
                    Text(
                        titleText,
                        style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                        color = Color.White
                    )
                }
            },
            text = { Text(bodyText, style = MaterialTheme.typography.bodyMedium, color = Color(0xFFA0A0A0)) },
            confirmButton = {
                Button(
                    onClick = {
                        onToggleEnable(targetItem, !targetItem.isEnabled)
                        toggleEnableTarget = null
                    },
                    shape = RoundedCornerShape(14.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = if (isSuspending) MaterialTheme.colorScheme.error else Color(0xFF10B981),
                        contentColor = Color.White
                    )
                ) {
                    Text(if (isSuspending) "CONFERMA SOSPESIONE" else "CONFERMA RIATTIVAZIONE", fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = {
                TextButton(onClick = { toggleEnableTarget = null }) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
            }
        )
    }

    var isDetailsVisible by remember { mutableStateOf(false) }

    LaunchedEffect(detailsRecurring) {
        if (detailsRecurring != null) {
            activeDetailsRecurring = detailsRecurring
            isDetailsVisible = true
        } else {
            isDetailsVisible = false
        }
    }

    LaunchedEffect(isDetailsVisible) {
        if (!isDetailsVisible && activeDetailsRecurring != null) {
            delay(220)
            detailsRecurring = null
            activeDetailsRecurring = null
        }
    }

    val dismissDetails = {
        isDetailsVisible = false
    }

    activeDetailsRecurring?.let { r ->
        val isIncome = r.type == "INCOME"
        val isTransfer = r.type == "TRANSFER" || r.toAccountId != null

        val acc = accounts.firstOrNull { it.id == r.accountId }
        val toAcc = accounts.firstOrNull { it.id == r.toAccountId }

        val displayEmoji = if (isTransfer) toAcc?.logoEmoji ?: r.logoEmoji else r.logoEmoji
        val displayUri = if (isTransfer) toAcc?.logoUri ?: r.logoUri else r.logoUri
        val fallbackName = if (isTransfer) toAcc?.name ?: r.name ?: "MOVIMENTO" else r.name ?: "MOVIMENTO"
        val catName = categories.firstOrNull { it.id == r.categoryId }?.name ?: "ALTRO"

        val nextDate = try { LocalDate.fromEpochDays(r.nextEpochDay.toInt()) } catch (_: Throwable) { Clock.System.todayIn(TimeZone.currentSystemDefault()) }
        val nextDateStr = "${nextDate.dayOfMonth.toString().padStart(2, '0')}/${nextDate.monthNumber.toString().padStart(2, '0')}/${nextDate.year}"

        val target = r.cycleTarget?.toLong() ?: 0L
        val isSinkingFund = target > 0L && !isIncome && !r.isCartella

        var accumulatedCents = 0L
        var currentStep = 0L
        var progress = 0f
        var isCycleComplete = false

        if (isSinkingFund) {
            val count = transactions.count { tx ->
                !tx.isDeleted &&
                        (tx.description?.contains("[rec:${r.id}") == true || tx.merchantKey?.contains("[rec:${r.id}") == true)
            }.toLong()

            currentStep = if (count == 0L) 0L else if (count % target == 0L) target else count % target
            accumulatedCents = currentStep * r.amountCents
            isCycleComplete = (currentStep >= target && target > 0L)
            progress = if (target > 0L) (currentStep.toFloat() / target.toFloat()).coerceAtMost(1f) else 0f
        }

        Dialog(
            onDismissRequest = { dismissDetails() },
            properties = DialogProperties(usePlatformDefaultWidth = false)
        ) {
            AnimatedVisibility(
                visible = isDetailsVisible,
                enter = fadeIn(tween(250)) + scaleIn(initialScale = 0.82f, animationSpec = spring(dampingRatio = Spring.DampingRatioMediumBouncy, stiffness = Spring.StiffnessLow)) + slideInVertically(initialOffsetY = { it / 3 }),
                exit = fadeOut(tween(200)) + scaleOut(targetScale = 0.85f) + slideOutVertically(targetOffsetY = { it / 3 })
            ) {
                Surface(
                    modifier = Modifier.padding(horizontal = 16.dp, vertical = 24.dp).fillMaxWidth().widthIn(max = 420.dp),
                    shape = RoundedCornerShape(28.dp),
                    color = Color.White.copy(alpha = 0.12f),
                    border = BorderStroke(1.5.dp, Color.White.copy(alpha = 0.40f)),
                    shadowElevation = 0.dp
                ) {
                    Column(modifier = Modifier.fillMaxWidth().padding(24.dp)) {
                        Row(verticalAlignment = Alignment.CenterVertically) {
                            TxLogo(displayEmoji, displayUri, fallbackName, Modifier.size(54.dp))
                            Spacer(Modifier.width(16.dp))
                            Column(modifier = Modifier.weight(1f)) {
                                Text(text = (r.name ?: "Automazione").uppercase(), style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Black, letterSpacing = 0.5.sp), color = Color.White, maxLines = 1, overflow = TextOverflow.Ellipsis)
                                Text(text = if(isTransfer) "GIROCONTO" else if (isIncome) "ENTRATA FISSA" else catName.uppercase(), style = MaterialTheme.typography.bodySmall, color = if(isIncome) incomeColor else brandPrimary, fontWeight = FontWeight.Bold)
                            }
                        }
                        Spacer(Modifier.height(20.dp))

                        if (isSinkingFund) {
                            Column(modifier = Modifier.fillMaxWidth()) {
                                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Bottom) {
                                    Text("Progresso Accumulo", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0))
                                    Text("${(progress * 100).toInt()}%", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.ExtraBold), color = brandPrimary)
                                }
                                Spacer(Modifier.height(8.dp))
                                LinearProgressIndicator(progress = { progress }, modifier = Modifier.fillMaxWidth().height(8.dp).clip(CircleShape), color = if (isCycleComplete) Color(0xFF10B981) else brandPrimary, trackColor = Color.White.copy(alpha = 0.1f))
                                Spacer(Modifier.height(16.dp))
                            }
                        }

                        @Composable
                        fun InfoRow(label: String, value: String = "", isBold: Boolean = false, color: Color = Color.White, moneyCents: Long? = null) {
                            Row(modifier = Modifier.fillMaxWidth().padding(vertical = 5.dp), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Bottom) {
                                Text(label, style = MaterialTheme.typography.bodyMedium, color = Color(0xFFA0A0A0))
                                if (moneyCents != null) {
                                    ModernMoneyText(cents = moneyCents, hideBalances = false, color = color, integerFontSize = 16.sp, decimalFontSize = 12.sp, symbolFontSize = 12.sp)
                                } else {
                                    Text(value, style = MaterialTheme.typography.bodyMedium.copy(fontWeight = if(isBold) FontWeight.ExtraBold else FontWeight.Medium), color = color)
                                }
                            }
                        }

                        InfoRow("Prossima data", nextDateStr)
                        InfoRow("Stato automazione", if (r.isEnabled) "Attiva" else "Sospesa", isBold = true, color = if (r.isEnabled) Color(0xFF10B981) else MaterialTheme.colorScheme.error)

                        if (isTransfer) {
                            InfoRow("Da", stripSystemMarker(acc?.name) ?: "Conto")
                            InfoRow("A", stripSystemMarker(toAcc?.name) ?: "Conto")
                        } else if (isIncome) {
                            InfoRow("Accredito su", stripSystemMarker(acc?.name) ?: "Conto")
                        } else {
                            InfoRow("Addebito su", stripSystemMarker(acc?.name) ?: "Conto")
                        }

                        Spacer(Modifier.height(12.dp))

                        Surface(
                            modifier = Modifier.fillMaxWidth(),
                            color = Color.White.copy(alpha = 0.08f),
                            shape = RoundedCornerShape(16.dp),
                            border = BorderStroke(1.dp, Color.White.copy(alpha = 0.15f))
                        ) {
                            Column(Modifier.padding(16.dp)) {
                                InfoRow(if (isTransfer) "Importo da spostare" else if (isIncome) "Entrata mensile" else "Costo mensile", moneyCents = r.amountCents, color = Color.White)
                                InfoRow(if (isTransfer) "Movimentazione annua" else if (isIncome) "Proiezione annuale" else "Impatto annuale", moneyCents = r.amountCents * 12, color = if (isIncome) incomeColor else if (isTransfer) brandPrimary else MaterialTheme.colorScheme.error)

                                if (isSinkingFund) {
                                    Spacer(Modifier.height(8.dp))
                                    HorizontalDivider(color = Color.White.copy(alpha = 0.08f))
                                    Spacer(Modifier.height(8.dp))
                                    InfoRow("Ciclo attuale", "$currentStep su $target rate")
                                    InfoRow("Accumulato", moneyCents = accumulatedCents, color = Color(0xFF10B981))
                                }
                            }
                        }

                        Spacer(Modifier.height(20.dp))

                        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceEvenly) {
                            RecurringActionIconBlock(Icons.Outlined.Delete, "Elimina", MaterialTheme.colorScheme.error, Modifier.weight(1f)) {
                                dismissDetails()
                                deleteTarget = r
                            }

                            RecurringActionIconBlock(Icons.Outlined.Edit, "Modifica", brandPrimary, Modifier.weight(1f)) {
                                dismissDetails()
                                if (r.isCartella) {
                                    recurringToEdit = r
                                    if (r.notes?.contains("[TAX:", ignoreCase = true) == true) {
                                        showTassaWizard = true
                                    } else {
                                        showCartellaWizard = true
                                    }
                                } else {
                                    onEditClick(r)
                                }
                            }

                            val actionIconVector = if (r.isEnabled) Icons.Outlined.VisibilityOff else Icons.Default.CheckCircle
                            RecurringActionIconBlock(actionIconVector, if (r.isEnabled) "Sospendi" else "Riattiva", brandPrimary, Modifier.weight(1f)) {
                                dismissDetails()
                                toggleEnableTarget = r
                            }

                            if (isSinkingFund) {
                                RecurringActionIconBlock(Icons.Default.AddCircleOutline, "Versa", brandPrimary, Modifier.weight(1f)) {
                                    dismissDetails()
                                    recurringToPay = r
                                }

                                if (accumulatedCents > 0L) {
                                    RecurringActionIconBlock(Icons.Default.Payments, "Liquida", Color(0xFF10B981), Modifier.weight(1f)) {
                                        dismissDetails()
                                        liquidateAmount = accumulatedCents
                                        liquidateMonths = currentStep
                                        liquidateTarget = r
                                    }

                                    if (isCycleComplete) {
                                        RecurringActionIconBlock(Icons.Default.Sync, "Rinnova", brandPrimary, Modifier.weight(1f)) {
                                            dismissDetails()
                                            cycleToManage = r
                                        }
                                    }
                                }
                            }
                        }

                        Spacer(Modifier.height(20.dp))
                        Button(
                            onClick = { dismissDetails() },
                            modifier = Modifier.fillMaxWidth().height(48.dp),
                            shape = RoundedCornerShape(14.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha = 0.12f), contentColor = Color.White)
                        ) { Text("CHIUDI", fontWeight = FontWeight.Bold) }
                    }
                }
            }
        }
    }

    if (showGenerationFeedback) {
        AlertDialog(
            onDismissRequest = { showGenerationFeedback = false },
            shape = RoundedCornerShape(28.dp),
            containerColor = Color.White.copy(alpha = 0.12f),
            title = {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Rounded.CheckCircle, null, tint = Color(0xFF10B981), modifier = Modifier.size(24.dp))
                    Spacer(Modifier.width(8.dp))
                    Text("Verifica Completata", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold), color = Color.White)
                }
            },
            text = { Text("Controllo automazioni terminato.", style = MaterialTheme.typography.bodyMedium, color = Color(0xFFA0A0A0)) },
            confirmButton = { TextButton(onClick = { showGenerationFeedback = false }) { Text("HO CAPITO", color = Color(0xFF60A5FA), fontWeight = FontWeight.Bold) } }
        )
    }

    cycleToManage?.let { rec ->
        CycleManagementDialog(
            recurringItem = rec,
            currentAccumulated = (rec.cycleTarget ?: 0L) * rec.amountCents,
            onDismiss = { cycleToManage = null },
            onAction = { updated -> onUpdate(updated); cycleToManage = null }
        )
    }

    liquidateTarget?.let { rec ->
        val initialEuros = liquidateAmount / 100
        val initialCents = (liquidateAmount % 100).toString().padStart(2, '0')
        var amountInput by remember(rec.id) { mutableStateOf("$initialEuros,$initialCents") }

        // ðŸš€ STATI PER IL CALENDARIO
        var showDatePicker by remember { mutableStateOf(false) }
        var selectedDateEpoch by remember { mutableStateOf(Clock.System.todayIn(TimeZone.currentSystemDefault()).toEpochDays().toLong()) }
        val datePickerState = rememberDatePickerState(initialSelectedDateMillis = Clock.System.now().toEpochMilliseconds())

        val selectedLocalDate = LocalDate.fromEpochDays(selectedDateEpoch.toInt())
        val dateStr = "${selectedLocalDate.dayOfMonth.toString().padStart(2, '0')}/${selectedLocalDate.monthNumber.toString().padStart(2, '0')}/${selectedLocalDate.year}"

        if (showDatePicker) {
            DatePickerDialog(
                onDismissRequest = { showDatePicker = false },
                confirmButton = {
                    TextButton(onClick = {
                        datePickerState.selectedDateMillis?.let { millis ->
                            val instant = kotlinx.datetime.Instant.fromEpochMilliseconds(millis)
                            selectedDateEpoch = instant.toLocalDateTime(TimeZone.UTC).date.toEpochDays().toLong()
                        }
                        showDatePicker = false
                    }) { Text("OK") }
                },
                dismissButton = {
                    TextButton(onClick = { showDatePicker = false }) { Text("ANNULLA") }
                }
            ) {
                DatePicker(state = datePickerState)
            }
        }

        AlertDialog(
            onDismissRequest = { liquidateTarget = null },
            shape = RoundedCornerShape(24.dp),
            title = {
                Text("Liquida Ciclo", style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold), color = MaterialTheme.colorScheme.primary)
            },
            text = {
                Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                    Text(
                        text = "Hai accumulato $liquidateMonths mesi, per un totale di ${formatEuroFromCents(liquidateAmount, false)}.\n\nScegli l'importo reale della tassa e la data in cui vuoi registrarla.",
                        style = MaterialTheme.typography.bodyMedium
                    )

                    OutlinedTextField(
                        value = amountInput,
                        onValueChange = { if (it.all { c -> c.isDigit() || c == ',' || c == '.' }) amountInput = it },
                        label = { Text("Importo reale da pagare (€)") },
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp)
                    )

                    // ðŸš€ CAMPO DATA CLICCABILE
                    Box(modifier = Modifier.fillMaxWidth().clickable { showDatePicker = true }) {
                        OutlinedTextField(
                            value = dateStr,
                            onValueChange = {},
                            readOnly = true,
                            label = { Text("Data di scadenza / pagamento") },
                            trailingIcon = { Icon(Icons.Default.DateRange, null, tint = MaterialTheme.colorScheme.primary) },
                            modifier = Modifier.fillMaxWidth(),
                            shape = RoundedCornerShape(12.dp),
                            enabled = false,
                            colors = OutlinedTextFieldDefaults.colors(
                                disabledTextColor = MaterialTheme.colorScheme.onSurface,
                                disabledBorderColor = MaterialTheme.colorScheme.outline,
                                disabledLabelColor = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        )
                    }
                }
            },
            confirmButton = {
                Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Button(onClick = {
                        val cleanInput = amountInput.replace(",", ".")
                        val actualCents = ((cleanInput.toDoubleOrNull() ?: 0.0) * 100).toLong()
                        // ðŸš€ PASSAGGIO DELLA DATA AL MOTORE CENTRALE
                        onLiquidateCycle(rec, liquidateAmount, actualCents, selectedDateEpoch, true)
                        liquidateTarget = null
                    }, modifier = Modifier.fillMaxWidth()) { Text("PAGA E RIPARTI DA ZERO") }

                    OutlinedButton(onClick = {
                        val cleanInput = amountInput.replace(",", ".")
                        val actualCents = ((cleanInput.toDoubleOrNull() ?: 0.0) * 100).toLong()
                        onLiquidateCycle(rec, liquidateAmount, actualCents, selectedDateEpoch, false)
                        liquidateTarget = null
                    }, modifier = Modifier.fillMaxWidth()) { Text("PAGA E SOSPENDI CICLO") }

                    TextButton(onClick = { liquidateTarget = null }, modifier = Modifier.fillMaxWidth()) { Text("ANNULLA", color = MaterialTheme.colorScheme.onSurfaceVariant) }
                }
            }
        )
    }

    // ... fine del blocco liquidateTarget?.let { ... }

    // ðŸš€ 3. AGGIUNGI QUESTO POPUP
    recurringToPay?.let { rec ->
        var isProcessing by remember { mutableStateOf(false) }

        AlertDialog(
            onDismissRequest = { if (!isProcessing) recurringToPay = null },
            shape = RoundedCornerShape(24.dp),
            containerColor = MaterialTheme.colorScheme.surface,
            title = {
                Text(
                    text = "Versa Rata Ciclo",
                    style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold),
                    color = MaterialTheme.colorScheme.primary
                )
            },
            text = {
                Text(
                    text = "Vuoi forzare manualmente il versamento di '${rec.name}' (${formatEuroFromCents(rec.amountCents)}) in data odierna?",
                    style = MaterialTheme.typography.bodyMedium
                )
            },
            confirmButton = {
                TextButton(
                    onClick = {
                        if (!isProcessing) {
                            isProcessing = true
                            onPayRecurringEarly(rec)
                            recurringToPay = null
                        }
                    },
                    enabled = !isProcessing
                ) {
                    Text("VERSA ORA", color = MaterialTheme.colorScheme.primary, fontWeight = FontWeight.Bold)
                }
            },
            dismissButton = {
                TextButton(
                    onClick = { recurringToPay = null },
                    enabled = !isProcessing
                ) { Text("ANNULLA", color = MaterialTheme.colorScheme.onSurfaceVariant) }
            }
        )
    }
} // <-- Questa è la parentesi graffa finale di RecurringScreen

@Composable
fun CycleManagementDialog(
    recurringItem: RecurringExpenseEntity,
    currentAccumulated: Long,
    onDismiss: () -> Unit,
    onAction: (RecurringExpenseEntity) -> Unit
) {
    var extraMonths by remember { mutableStateOf("6") }

    AlertDialog(
        onDismissRequest = onDismiss,
        shape = RoundedCornerShape(24.dp),
        title = {
            Column {
                Text("ðŸŽ¯ OBIETTIVO RAGGIUNTO!", style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold), color = Color(0xFF10B981))
                Text((recurringItem.name ?: "Automazione").uppercase(), style = MaterialTheme.typography.labelMedium)
            }
        },
        text = {
            Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                Text("Hai accumulato ${formatEuroFromCents(currentAccumulated, false)}. Scegli come procedere:")
                OutlinedTextField(
                    value = extraMonths,
                    onValueChange = { if (it.all { c -> c.isDigit() }) extraMonths = it },
                    label = { Text("Mesi da aggiungere") },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(12.dp)
                )
            }
        },
        confirmButton = {
            Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Button(onClick = {
                    val added = extraMonths.toLongOrNull() ?: 0L
                    onAction(recurringItem.copy(cycleTarget = (recurringItem.cycleTarget ?: 0L) + added, updatedAtEpochMs = Clock.System.now().toEpochMilliseconds()))
                }, modifier = Modifier.fillMaxWidth()) { Text("CONTINUA ACCUMULO") }

                OutlinedButton(onClick = {
                    onAction(recurringItem.copy(startEpochDay = Clock.System.todayIn(TimeZone.currentSystemDefault()).toEpochDays().toLong(), updatedAtEpochMs = Clock.System.now().toEpochMilliseconds()))
                }, modifier = Modifier.fillMaxWidth()) { Text("REINIZIA DA ZERO") }

                TextButton(onClick = {
                    onAction(recurringItem.copy(isEnabled = false, updatedAtEpochMs = Clock.System.now().toEpochMilliseconds()))
                }, modifier = Modifier.fillMaxWidth()) { Text("INTERROMPI", color = MaterialTheme.colorScheme.error) }
            }
        }
    )
}

fun formatEuroFromCents(cents: Long, showSign: Boolean = true): String {
    val sign = if (cents < 0 && showSign) "-" else ""
    val absoluteCents = kotlin.math.abs(cents)
    val euros = absoluteCents / 100
    val remainderCents = absoluteCents % 100
    val centsStr = remainderCents.toString().padStart(2, '0')
    return "$sign$euros,$centsStr €"
}

@Composable
private fun RecurringActionIconBlock(
    icon: ImageVector,
    label: String,
    color: Color,
    modifier: Modifier = Modifier, // ðŸš€ Aggiunto per accettare il weight(1f)
    onClick: () -> Unit
) {
    Column(
        horizontalAlignment = Alignment.CenterHorizontally,
        modifier = modifier.clickable { onClick() }.padding(vertical = 8.dp, horizontal = 2.dp)
    ) {
        Icon(icon, contentDescription = null, tint = color, modifier = Modifier.size(26.dp))
        Spacer(Modifier.height(4.dp))
        Text(
            text = label,
            style = MaterialTheme.typography.labelSmall.copy(fontSize = 10.sp), // ðŸš€ Font ridotto leggermente
            color = color,
            maxLines = 1, // ðŸš€ Niente ritorni a capo
            overflow = TextOverflow.Ellipsis // ðŸš€ Se serve, mette i "..." invece di rompere la UI
        )
    }
}

// ==========================================
// I COMPONENTI DEL WIZARD PER LE CARTELLE
// ==========================================

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun TassaWizardDialog(
    activeAccountId: String?,
    accounts: List<AccountEntity> = emptyList(),
    onDismiss: () -> Unit,
    onSaveRecurring: (RecurringExpenseEntity) -> Unit,
    onSaveTransaction: ((TransactionEntity) -> Unit)? = null,
    editingEntity: RecurringExpenseEntity? = null,
    payPeriodEnd: LocalDate? = null
) {
    val isEditing = editingEntity != null
    val isTax = editingEntity?.notes?.contains("[TAX:", ignoreCase = true) == true || (activeAccountId == "NEW_TAX_MODE")

    val resolvedAccountId = if (editingEntity?.accountId != null && editingEntity.accountId != "NEW_TAX_MODE" && editingEntity.accountId!!.isNotBlank()) {
        editingEntity.accountId!!
    } else if (activeAccountId != null && activeAccountId != "NEW_TAX_MODE" && activeAccountId.isNotBlank()) {
        activeAccountId
    } else {
        accounts.firstOrNull { !it.isDeleted && !it.isArchived && !it.id.startsWith("sys_") }?.id
            ?: accounts.firstOrNull { !it.isDeleted }?.id
            ?: ""
    }

    var name by remember { mutableStateOf(editingEntity?.name ?: if (isTax) "Tassa Comunale (Es. IMU)" else "Cartella Agenzia Entrate") }

    val existingRates = editingEntity?.notes?.let { note ->
        if (isTax) {
            Regex("\\[TAX:([^\\]]*)\\]", RegexOption.IGNORE_CASE).find(note)?.groupValues?.get(1)?.split(",")?.map { it.trim() }?.filter { it.isNotEmpty() }
        } else {
            Regex("\\[CART:([^\\]]*)\\]", RegexOption.IGNORE_CASE).find(note)?.groupValues?.get(1)?.split(",")?.map { it.trim() }?.filter { it.isNotEmpty() }
        }
    } ?: emptyList()
    var numRateText by remember { mutableStateOf(if (isEditing) existingRates.size.toString() else if (isTax) "2" else "4") }

    var rateAmounts by remember {
        mutableStateOf(
            if (isEditing) {
                existingRates.map {
                    val baseAmt = if (isTax) it.split("@").firstOrNull() ?: "0" else it
                    val vCents = baseAmt.replace(Regex(":[PX]", RegexOption.IGNORE_CASE), "").toLongOrNull() ?: 0L
                    val vEuros = vCents / 100.0
                    if (vEuros % 1.0 == 0.0) vEuros.toLong().toString() else vEuros.toString()
                }.toMutableList()
            } else List(if (isTax) 2 else 4) { "" }.toMutableList()
        )
    }

    val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
    var startMonth by remember { mutableStateOf(editingEntity?.let { LocalDate.fromEpochDays(it.startEpochDay.toInt()) } ?: today) }
    var dayInput by remember(startMonth.dayOfMonth) { mutableStateOf(startMonth.dayOfMonth.toString()) }

    var rateTargetMonths by remember {
        mutableStateOf(
            if (isEditing) {
                existingRates.map {
                    it.split("@").getOrNull(1)?.replace(Regex(":[PX]", RegexOption.IGNORE_CASE), "")?.toIntOrNull() ?: startMonth.monthNumber
                }.toMutableList()
            } else {
                List(numRateText.toIntOrNull()?.coerceIn(1, 120) ?: 2) { idx ->
                    var m = startMonth.monthNumber + (idx * 6)
                    while (m > 12) m -= 12
                    m
                }.toMutableList()
            }
        )
    }

    var rateTargetYears by remember {
        mutableStateOf(
            if (isEditing) {
                existingRates.map { _ ->
                    startMonth.year
                }.toMutableList()
            } else {
                List(numRateText.toIntOrNull()?.coerceIn(1, 120) ?: 2) { idx ->
                    var m = startMonth.monthNumber + (idx * 6)
                    var y = startMonth.year
                    while (m > 12) { m -= 12; y++ }
                    y
                }.toMutableList()
            }
        )
    }

    // 0 = Pending (Circle), 1 = Paid (Green Check :P), 2 = Skipped (Red X :X)
    var ratePaidStatus by remember {
        mutableStateOf(
            if (isEditing) {
                existingRates.map {
                    when {
                        it.endsWith(":P", ignoreCase = true) -> 1
                        it.endsWith(":X", ignoreCase = true) -> 2
                        else -> 0
                    }
                }.toMutableList()
            } else List(if (isTax) 2 else 4) { 0 }.toMutableList()
        )
    }
    var actionDialogIndex by remember { mutableStateOf<Int?>(null) }

    LaunchedEffect(startMonth, dayInput, numRateText, rateTargetMonths, rateTargetYears) {
        if (!isEditing) {
            val num = numRateText.toIntOrNull()?.coerceIn(1, 120) ?: return@LaunchedEffect
            val d = dayInput.toIntOrNull() ?: startMonth.dayOfMonth
            val calToday = Clock.System.todayIn(TimeZone.currentSystemDefault())
            val todayEpoch = calToday.toEpochDays()
            
            val newStatusList = ratePaidStatus.toMutableList()
            while (newStatusList.size < num) newStatusList.add(0)
            while (newStatusList.size > num) newStatusList.removeAt(newStatusList.size - 1)
            
            for (i in newStatusList.indices) {
                val tm = rateTargetMonths.getOrElse(i) { startMonth.monthNumber }
                val ty = rateTargetYears.getOrElse(i) { startMonth.year }
                val maxD = when (tm) { 2 -> if (ty % 4 == 0 && (ty % 100 != 0 || ty % 400 == 0)) 29 else 28; 4, 6, 9, 11 -> 30; else -> 31 }
                val targetDay = d.coerceAtMost(maxD)
                val epoch = LocalDate(ty, tm, targetDay).toEpochDays()
                
                newStatusList[i] = if (epoch < todayEpoch) 1 else 0
            }
            ratePaidStatus = newStatusList
        }
    }

    LaunchedEffect(numRateText) {
        val num = numRateText.toIntOrNull()?.coerceIn(1, 120) ?: return@LaunchedEffect
        if (num != rateAmounts.size) {
            val newList = rateAmounts.toMutableList()
            val newStatusList = ratePaidStatus.toMutableList()
            val newMonthsList = rateTargetMonths.toMutableList()
            val newYearsList = rateTargetYears.toMutableList()

            while (newList.size < num) {
                newList.add("")
                newStatusList.add(0)
                val lastM = newMonthsList.lastOrNull() ?: startMonth.monthNumber
                val lastY = newYearsList.lastOrNull() ?: startMonth.year
                var nextM = lastM + 6
                var nextY = lastY
                while (nextM > 12) { nextM -= 12; nextY++ }
                newMonthsList.add(nextM)
                newYearsList.add(nextY)
            }
            while (newList.size > num) {
                newList.removeAt(newList.size - 1)
                newStatusList.removeAt(newStatusList.size - 1)
                newMonthsList.removeAt(newMonthsList.size - 1)
                newYearsList.removeAt(newYearsList.size - 1)
            }
            rateAmounts = newList
            ratePaidStatus = newStatusList
            rateTargetMonths = newMonthsList
            rateTargetYears = newYearsList
        }
    }

    actionDialogIndex?.let { index ->
        val targetMonth = rateTargetMonths.getOrElse(index) { startMonth.monthNumber }
        val targetYear = rateTargetYears.getOrElse(index) { startMonth.year }

        AlertDialog(
            onDismissRequest = { actionDialogIndex = null },
            shape = RoundedCornerShape(24.dp),
            containerColor = Color(0xFF1E1B4B),
            title = {
                Text("Rata di $targetMonth/$targetYear", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold), color = Color.White)
            },
            text = {
                Text("Vuoi scalare i soldi dal saldo (creando un movimento di spesa) o vuoi solo segnarla come pagata?", style = MaterialTheme.typography.bodyMedium, color = Color(0xFFA0A0A0))
            },
            confirmButton = {
                Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Button(
                        onClick = {
                            val mutStatus = ratePaidStatus.toMutableList()
                            mutStatus[index] = 1
                            ratePaidStatus = mutStatus

                            val nowMs = Clock.System.now().toEpochMilliseconds()
                            val cartellaId = editingEntity?.id ?: "cart_$nowMs"
                            val amtCents = (rateAmounts.getOrNull(index)?.replace(",", ".")?.toDoubleOrNull()?.times(100))?.toLong() ?: 0L

                            if (amtCents > 0L) {
                                val tx = TransactionEntity(
                                    id = "tx_${nowMs}_${Random.nextInt(1000)}",
                                    accountId = resolvedAccountId,
                                    toAccountId = null,
                                    fromAccountId = null,
                                    amountCents = amtCents,
                                    type = "EXPENSE",
                                    dateEpochDay = Clock.System.todayIn(TimeZone.currentSystemDefault()).toEpochDays().toLong(),
                                    categoryId = editingEntity?.categoryId ?: "default",
                                    description = "$name - Rata ${index + 1}",
                                    merchantKey = "[rec:$cartellaId] [manual]",
                                    subcategoryText = "Tassa Comunale",
                                    logoEmoji = "🏛️",
                                    logoUri = null,
                                    isHidden = false,
                                    isDeleted = false,
                                    reminderDays = -1L,
                                    createdAtEpochMs = nowMs,
                                    updatedAtEpochMs = nowMs
                                )
                                onSaveTransaction?.invoke(tx)
                            }
                            actionDialogIndex = null
                        },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF10B981), contentColor = Color.White)
                    ) {
                        Text("SCALA I SOLDI E SEGNA PAGATA", fontWeight = FontWeight.Bold)
                    }

                    Button(
                        onClick = {
                            val mutStatus = ratePaidStatus.toMutableList()
                            mutStatus[index] = 1
                            ratePaidStatus = mutStatus
                            actionDialogIndex = null
                        },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha = 0.15f), contentColor = Color.White)
                    ) {
                        Text("SOLO SEGNA PAGATA", fontWeight = FontWeight.Bold)
                    }

                    TextButton(
                        onClick = { actionDialogIndex = null },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text("ANNULLA", fontWeight = FontWeight.Bold, color = Color.White.copy(alpha = 0.6f))
                    }
                }
            },
            dismissButton = null
        )
    }

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        Surface(
            shape = RoundedCornerShape(28.dp),
            color = Color.White.copy(alpha = 0.12f),
            border = BorderStroke(1.5.dp, Color.White.copy(alpha = 0.40f)),
            shadowElevation = 24.dp,
            modifier = Modifier.fillMaxWidth(0.95f).fillMaxHeight(0.85f).imePadding()
        ) {
            Column(modifier = Modifier.fillMaxSize().padding(24.dp).verticalScroll(rememberScrollState())) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Rounded.AccountBalance, null, tint = Color(0xFFF59E0B))
                    Spacer(Modifier.width(8.dp))
                    Text(if (isEditing) "Modifica Tassa" else "Nuova Tassa / Imposta", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Black, color = Color.White)
                }
                Text("Tocca i cerchi vuoti per marcare o smarcare le rate pagate.", style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0))

                Spacer(Modifier.height(16.dp))

                OutlinedTextField(
                    value = name, onValueChange = { name = it },
                    label = { Text("Nome Tassa / Tributo", color = Color(0xFFA0A0A0)) },
                    modifier = Modifier.fillMaxWidth(), singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                Spacer(Modifier.height(8.dp))

                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedTextField(
                        value = numRateText, onValueChange = { numRateText = it },
                        label = { Text("N° Rate", color = Color(0xFFA0A0A0)) },
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        modifier = Modifier.weight(0.5f), singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                    )

                    OutlinedTextField(
                        value = dayInput,
                        onValueChange = { if (it.all { c -> c.isDigit() }) dayInput = it },
                        label = { Text("Giorno di addebito (1-31)", color = Color(0xFFA0A0A0)) },
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        modifier = Modifier.weight(0.5f), singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                    )
                }

                Spacer(Modifier.height(16.dp))
                HorizontalDivider(color = Color.White.copy(alpha = 0.15f))
                Spacer(Modifier.height(8.dp))

                Column {
                    rateAmounts.forEachIndexed { index, _ ->
                        val targetMonth = rateTargetMonths.getOrElse(index) { startMonth.monthNumber }
                        val targetYear = rateTargetYears.getOrElse(index) { startMonth.year }

                        val statusInt = ratePaidStatus.getOrElse(index) { 0 }

                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)
                        ) {
                            Icon(
                                imageVector = when (statusInt) { 1 -> Icons.Rounded.CheckCircle; 2 -> Icons.Default.Close; else -> Icons.Rounded.RadioButtonUnchecked },
                                contentDescription = null,
                                tint = when (statusInt) { 1 -> Color(0xFF10B981); 2 -> MaterialTheme.colorScheme.error; else -> Color.White.copy(alpha = 0.3f) },
                                modifier = Modifier
                                    .size(32.dp)
                                    .padding(end = 6.dp)
                                    .clip(CircleShape)
                                    .clickable {
                                        val mutStatus = ratePaidStatus.toMutableList()
                                        if (statusInt == 2) {
                                            actionDialogIndex = index
                                        } else if (statusInt == 0) {
                                            mutStatus[index] = 1
                                            ratePaidStatus = mutStatus
                                        } else if (statusInt == 1) {
                                            val todayE = Clock.System.todayIn(TimeZone.currentSystemDefault()).toEpochDays()
                                            val maxD = when (targetMonth) { 2 -> if (targetYear % 4 == 0 && (targetYear % 100 != 0 || targetYear % 400 == 0)) 29 else 28; 4, 6, 9, 11 -> 30; else -> 31 }
                                            val epoch = LocalDate(targetYear, targetMonth, startMonth.dayOfMonth.coerceAtMost(maxD)).toEpochDays()
                                            mutStatus[index] = if (epoch < todayE) 2 else 0
                                            ratePaidStatus = mutStatus
                                        }
                                    }
                            )

                            // Selettore Mese/Anno della SINGOLA RATA
                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                modifier = Modifier.weight(1.2f)
                            ) {
                                Text("R ${index + 1}:", style = MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.Bold), color = Color.White)

                                IconButton(
                                    onClick = {
                                        var m = targetMonth - 1
                                        var y = targetYear
                                        if (m < 1) { m = 12; y-- }
                                        val mutM = rateTargetMonths.toMutableList()
                                        val mutY = rateTargetYears.toMutableList()
                                        mutM[index] = m
                                        mutY[index] = y
                                        rateTargetMonths = mutM
                                        rateTargetYears = mutY
                                    },
                                    modifier = Modifier.size(28.dp)
                                ) {
                                    Icon(Icons.Default.ChevronLeft, "Mese Precedente", tint = Color(0xFF60A5FA), modifier = Modifier.size(18.dp))
                                }

                                Text(
                                    text = "${targetMonth.toString().padStart(2, '0')}/$targetYear",
                                    style = MaterialTheme.typography.bodySmall.copy(fontWeight = FontWeight.Bold),
                                    color = Color.White,
                                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                                )

                                IconButton(
                                    onClick = {
                                        var m = targetMonth + 1
                                        var y = targetYear
                                        if (m > 12) { m = 1; y++ }
                                        val mutM = rateTargetMonths.toMutableList()
                                        val mutY = rateTargetYears.toMutableList()
                                        mutM[index] = m
                                        mutY[index] = y
                                        rateTargetMonths = mutM
                                        rateTargetYears = mutY
                                    },
                                    modifier = Modifier.size(28.dp)
                                ) {
                                    Icon(Icons.Default.ChevronRight, "Mese Successivo", tint = Color(0xFF60A5FA), modifier = Modifier.size(18.dp))
                                }
                            }

                            OutlinedTextField(
                                value = rateAmounts[index],
                                onValueChange = { newVal ->
                                    if (newVal.all { it.isDigit() || it == ',' || it == '.' }) {
                                        val mutList = rateAmounts.toMutableList()
                                        mutList[index] = newVal
                                        rateAmounts = mutList
                                    }
                                },
                                label = { Text("€", color = Color(0xFFA0A0A0)) },
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                                modifier = Modifier.weight(0.8f), singleLine = true,
                                colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                            )
                        }
                    }
                }

                val totalSum = rateAmounts.sumOf { (it.replace(",", ".").toDoubleOrNull() ?: 0.0) }

                Spacer(Modifier.height(8.dp))
                val totalSumCents = (totalSum * 100).toLong()
                Row(verticalAlignment = Alignment.Bottom) {
                    Text("Totale: ", style = MaterialTheme.typography.titleMedium, color = MaterialTheme.colorScheme.error, fontWeight = FontWeight.Black, modifier = Modifier.padding(bottom = 1.dp))
                    ModernMoneyText(cents = totalSumCents, hideBalances = false, color = MaterialTheme.colorScheme.error, integerFontSize = 20.sp, decimalFontSize = 14.sp, symbolFontSize = 14.sp)
                }
                Spacer(Modifier.height(16.dp))

                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
                    Spacer(Modifier.width(12.dp))
                    Button(
                        onClick = {
                            val nowMs = Clock.System.now().toEpochMilliseconds()
                            val cartellaId = editingEntity?.id ?: "cart_${nowMs}"

                            val rateCents = rateAmounts.mapNotNull { (it.replace(",", ".").toDoubleOrNull()?.times(100))?.toLong() }
                            if (rateCents.isEmpty()) return@Button

                            val todayObj = Clock.System.todayIn(TimeZone.currentSystemDefault())
                            val todayEpoch = todayObj.toEpochDays().toLong()
                            val maxDaysInMonth = when (todayObj.monthNumber) { 2 -> if (todayObj.year % 4 == 0 && (todayObj.year % 100 != 0 || todayObj.year % 400 == 0)) 29 else 28; 4, 6, 9, 11 -> 30; else -> 31 }
                            val endOfPeriodEpoch = (payPeriodEnd ?: LocalDate(todayObj.year, todayObj.monthNumber, maxDaysInMonth)).toEpochDays().toLong()

                            val newRatesList = mutableListOf<String>()
                            var firstUnpaidAmount = -1L
                            var resolvedCount = 0

                            val finalStatusList = ratePaidStatus.toMutableList()

                            val dayVal = dayInput.toIntOrNull()?.coerceIn(1, 31) ?: todayObj.dayOfMonth
                            val firstRateMonth = rateTargetMonths.getOrElse(0) { todayObj.monthNumber }
                            val firstRateYear = rateTargetYears.getOrElse(0) { todayObj.year }
                            val maxDFirst = when (firstRateMonth) { 2 -> if (firstRateYear % 4 == 0 && (firstRateYear % 100 != 0 || firstRateYear % 400 == 0)) 29 else 28; 4, 6, 9, 11 -> 30; else -> 31 }
                            val calculatedStartEpochDay = LocalDate(firstRateYear, firstRateMonth, dayVal.coerceAtMost(maxDFirst)).toEpochDays().toLong()

                            for (i in rateCents.indices) {
                                val targetMonth = rateTargetMonths.getOrElse(i) { todayObj.monthNumber }
                                val targetYear = rateTargetYears.getOrElse(i) { todayObj.year }
                                val maxD = when (targetMonth) { 2 -> if (targetYear % 4 == 0 && (targetYear % 100 != 0 || targetYear % 400 == 0)) 29 else 28; 4, 6, 9, 11 -> 30; else -> 31 }
                                val rateEpochDay = LocalDate(targetYear, targetMonth, dayVal.coerceAtMost(maxD)).toEpochDays().toLong()

                                var statusInt = finalStatusList.getOrElse(i) { 0 }

                                if (!isEditing && statusInt == 0) {
                                    if (rateEpochDay < todayEpoch) {
                                        // Passato (retroattiva): Segnamo come pagata (:P) con spunta verde, NESSUN movimento in uscita!
                                        statusInt = 1
                                        finalStatusList[i] = 1
                                    } else if (rateEpochDay == todayEpoch) {
                                        // Oggi (giorno di inserimento): Segnamo come pagata (:P) e creiamo subito il movimento oggi!
                                        statusInt = 1
                                        finalStatusList[i] = 1

                                        if (rateCents[i] > 0L) {
                                            val subcatLabel = "Tassa Comunale"
                                            val tx = TransactionEntity(
                                                id = "tx_${nowMs}_${i}_${Random.nextInt(1000)}",
                                                accountId = resolvedAccountId,
                                                toAccountId = null,
                                                fromAccountId = null,
                                                amountCents = rateCents[i],
                                                type = "EXPENSE",
                                                dateEpochDay = todayEpoch,
                                                categoryId = editingEntity?.categoryId ?: "default",
                                                description = "$name - Rata ${i + 1}",
                                                merchantKey = "[rec:$cartellaId] [manual]",
                                                subcategoryText = subcatLabel,
                                                logoEmoji = "🏛️",
                                                logoUri = null,
                                                isHidden = false,
                                                isDeleted = false,
                                                reminderDays = -1L,
                                                createdAtEpochMs = nowMs + i,
                                                updatedAtEpochMs = nowMs + i
                                            )
                                            onSaveTransaction?.invoke(tx)
                                        }
                                    }
                                }

                                val isResolved = (statusInt == 1 || statusInt == 2)
                                if (isResolved) resolvedCount++

                                val rateStr = when (statusInt) {
                                    1 -> "${rateCents[i]}@${targetMonth}:P"
                                    2 -> "${rateCents[i]}@${targetMonth}:X"
                                    else -> "${rateCents[i]}@${targetMonth}"
                                }
                                newRatesList.add(rateStr)

                                if (statusInt == 0 && firstUnpaidAmount == -1L) {
                                    firstUnpaidAmount = rateCents[i]
                                }
                            }

                            val firstUnpaidIndex = finalStatusList.indexOfFirst { it == 0 }
                            val infallibileNextEpochDay = if (firstUnpaidIndex != -1) {
                                val tm = rateTargetMonths.getOrElse(firstUnpaidIndex) { firstRateMonth }
                                val ty = rateTargetYears.getOrElse(firstUnpaidIndex) { firstRateYear }
                                val maxD = when (tm) { 2 -> if (ty % 4 == 0 && (ty % 100 != 0 || ty % 400 == 0)) 29 else 28; 4, 6, 9, 11 -> 30; else -> 31 }
                                LocalDate(ty, tm, dayVal.coerceAtMost(maxD)).toEpochDays().toLong()
                            } else {
                                val lastIdx = (rateCents.size - 1).coerceAtLeast(0)
                                val tm = rateTargetMonths.getOrElse(lastIdx) { firstRateMonth }
                                val ty = rateTargetYears.getOrElse(lastIdx) { firstRateYear }
                                val maxD = when (tm) { 2 -> if (ty % 4 == 0 && (ty % 100 != 0 || ty % 400 == 0)) 29 else 28; 4, 6, 9, 11 -> 30; else -> 31 }
                                LocalDate(ty, tm, dayVal.coerceAtMost(maxD)).toEpochDays().toLong()
                            }

                            val finalAmount = if (firstUnpaidAmount != -1L) firstUnpaidAmount else 0L
                            val finalNotesCart = newRatesList.joinToString(",")
                            val isArchivedNow = firstUnpaidAmount == -1L

                            val notesTag = if (isTax) "[TAX:$finalNotesCart]" else "[CART:$finalNotesCart]"
                            val notesStr = notesTag + (if (isArchivedNow || editingEntity?.notes?.contains("[ARCHIVED:true]") == true) " [ARCHIVED:true]" else "")

                            val newRec = RecurringExpenseEntity(
                                id = cartellaId,
                                name = name,
                                type = "EXPENSE",
                                amountCents = finalAmount,
                                accountId = resolvedAccountId,
                                toAccountId = null,
                                categoryId = editingEntity?.categoryId ?: "default",
                                dayOfMonth = dayVal.toLong(),
                                isEnabled = editingEntity?.isEnabled ?: !isArchivedNow,
                                isDeleted = false,
                                cycleTarget = rateCents.size.toLong(),
                                logoEmoji = editingEntity?.logoEmoji ?: "🏛️",
                                logoUri = editingEntity?.logoUri,
                                notes = notesStr,
                                autoGenerateOnReminder = true,
                                nextEpochDay = infallibileNextEpochDay,
                                frequency = editingEntity?.frequency ?: "Mensile",
                                isPaused = editingEntity?.isPaused ?: false,
                                startEpochDay = editingEntity?.startEpochDay ?: calculatedStartEpochDay,
                                createdAtEpochMs = editingEntity?.createdAtEpochMs ?: nowMs,
                                updatedAtEpochMs = nowMs
                            )

                            onSaveRecurring(newRec)
                        },
                        shape = RoundedCornerShape(14.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error, contentColor = Color.White)
                    ) {
                        Text("SALVA PIANO", fontWeight = FontWeight.Bold)
                    }
                }
            }
        }
    }
}

private fun is100PercentCompleted(rec: RecurringExpenseEntity): Boolean {
    val isTax = rec.notes?.contains("[TAX:", ignoreCase = true) == true
    val notes = rec.notes ?: ""
    val cartellaRegex = if (isTax) Regex("\\[TAX:([^\\]]*)\\]", RegexOption.IGNORE_CASE) else Regex("\\[CART:([^\\]]*)\\]", RegexOption.IGNORE_CASE)
    val match = cartellaRegex.find(notes)
    val rates = match?.groupValues?.get(1)?.split(",")?.map { it.trim() }?.filter { it.isNotBlank() } ?: emptyList()
    if (rates.isEmpty()) return false

    val importoPagato = rates.filter { it.endsWith(":P", ignoreCase = true) }.sumOf { 
        if (isTax) {
            it.split("@").firstOrNull()?.replace(Regex(":[PX]", RegexOption.IGNORE_CASE), "")?.toLongOrNull() ?: 0L
        } else {
            it.replace(Regex(":[PX]", RegexOption.IGNORE_CASE), "").toLongOrNull() ?: 0L
        }
    }

    val totaleCartella = rates.sumOf { 
        if (isTax) {
            it.split("@").firstOrNull()?.replace(Regex(":[PX]", RegexOption.IGNORE_CASE), "")?.toLongOrNull() ?: 0L
        } else {
            it.replace(Regex(":[PX]", RegexOption.IGNORE_CASE), "").toLongOrNull() ?: 0L
        }
    }

    return totaleCartella > 0L && importoPagato >= totaleCartella
}

@Composable
fun TaxBillsTrackerCard(
    recurringItems: List<RecurringExpenseEntity>,
    pastTransactions: List<TransactionEntity>,
    onEditCartella: (RecurringExpenseEntity) -> Unit,
    onDeleteCartella: (RecurringExpenseEntity) -> Unit,
    onEstinguiCartella: (RecurringExpenseEntity) -> Unit,
    onOpenDetails: (RecurringExpenseEntity) -> Unit,
    hideBalances: Boolean
) {
    val allTaxBills = remember(recurringItems) {
        recurringItems.filter { !it.isDeleted && it.isCartella }
    }

    if (allTaxBills.isEmpty()) return

    val (estintiList, attiviList) = remember(allTaxBills) {
        allTaxBills.partition { is100PercentCompleted(it) }
    }

    var selectedTab by remember { mutableStateOf(0) } // 0 = Attivi, 1 = Estinti

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(24.dp),
        color = Color.White.copy(alpha = 0.08f),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.error.copy(alpha = 0.35f))
    ) {
        Column(modifier = Modifier.padding(20.dp)) {

            // TAB BAR (ATTIVI vs ESTINTI)
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .clip(RoundedCornerShape(16.dp))
                    .background(Color.Black.copy(alpha = 0.25f))
                    .padding(4.dp),
                horizontalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                val attiviCount = attiviList.size
                val estintiCount = estintiList.size

                Surface(
                    onClick = { selectedTab = 0 },
                    modifier = Modifier.weight(1f).height(42.dp),
                    shape = RoundedCornerShape(12.dp),
                    color = if (selectedTab == 0) MaterialTheme.colorScheme.error.copy(alpha = 0.85f) else Color.Transparent
                ) {
                    Row(
                        horizontalArrangement = Arrangement.Center,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            Icons.Rounded.AccountBalance,
                            contentDescription = null,
                            tint = if (selectedTab == 0) Color.White else Color.White.copy(alpha = 0.6f),
                            modifier = Modifier.size(18.dp)
                        )
                        Spacer(Modifier.width(6.dp))
                        Text(
                            "Attivi ($attiviCount)",
                            style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold),
                            color = if (selectedTab == 0) Color.White else Color.White.copy(alpha = 0.6f)
                        )
                    }
                }

                Surface(
                    onClick = { selectedTab = 1 },
                    modifier = Modifier.weight(1f).height(42.dp),
                    shape = RoundedCornerShape(12.dp),
                    color = if (selectedTab == 1) Color(0xFF10B981).copy(alpha = 0.85f) else Color.Transparent
                ) {
                    Row(
                        horizontalArrangement = Arrangement.Center,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            Icons.Rounded.CheckCircle,
                            contentDescription = null,
                            tint = if (selectedTab == 1) Color.White else Color.White.copy(alpha = 0.6f),
                            modifier = Modifier.size(18.dp)
                        )
                        Spacer(Modifier.width(6.dp))
                        Text(
                            "Estinti 100% ($estintiCount)",
                            style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold),
                            color = if (selectedTab == 1) Color.White else Color.White.copy(alpha = 0.6f)
                        )
                    }
                }
            }

            Spacer(Modifier.height(16.dp))

            if (selectedTab == 0) {
                // TAB ATTIVI
                if (attiviList.isEmpty()) {
                    Text(
                        "Nessuna cartella o tassa attiva.",
                        style = MaterialTheme.typography.bodySmall,
                        color = Color.White.copy(alpha = 0.5f),
                        modifier = Modifier.padding(vertical = 12.dp)
                    )
                } else {
                    attiviList.forEach { item ->
                        val isTax = item.notes?.contains("[TAX:", ignoreCase = true) == true
                        CartellaItemView(
                            cartella = item,
                            pastTransactions = pastTransactions,
                            isArchived = false,
                            onEditCartella = onEditCartella,
                            onDeleteCartella = onDeleteCartella,
                            onEstinguiCartella = onEstinguiCartella,
                            onOpenDetails = onOpenDetails,
                            hideBalances = hideBalances,
                            isTax = isTax
                        )
                    }
                }
            } else {
                // TAB ESTINTI (solo quelli al 100%)
                if (estintiList.isEmpty()) {
                    Text(
                        "Nessuna cartella o tassa estinta al 100%.",
                        style = MaterialTheme.typography.bodySmall,
                        color = Color.White.copy(alpha = 0.5f),
                        modifier = Modifier.padding(vertical = 12.dp)
                    )
                } else {
                    estintiList.forEach { item ->
                        val isTax = item.notes?.contains("[TAX:", ignoreCase = true) == true
                        CartellaItemView(
                            cartella = item,
                            pastTransactions = pastTransactions,
                            isArchived = true,
                            onEditCartella = onEditCartella,
                            onDeleteCartella = onDeleteCartella,
                            onEstinguiCartella = onEstinguiCartella,
                            onOpenDetails = onOpenDetails,
                            hideBalances = hideBalances,
                            isTax = isTax
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun CartellaItemView(
    cartella: RecurringExpenseEntity,
    pastTransactions: List<TransactionEntity>,
    isArchived: Boolean,
    onEditCartella: (RecurringExpenseEntity) -> Unit,
    onDeleteCartella: (RecurringExpenseEntity) -> Unit,
    onEstinguiCartella: (RecurringExpenseEntity) -> Unit,
    onOpenDetails: (RecurringExpenseEntity) -> Unit,
    hideBalances: Boolean,
    isTax: Boolean = false
) {    val nomeCartella = cartella.name ?: "Cartella"

    val notes = cartella.notes ?: ""
    val cartellaRegex = if (isTax) Regex("\\[TAX:([^\\]]*)\\]", RegexOption.IGNORE_CASE) else Regex("\\[CART:([^\\]]*)\\]", RegexOption.IGNORE_CASE)
    val match = cartellaRegex.find(notes)
    val rates = match?.groupValues?.get(1)?.split(",")?.map { it.trim() }?.filter { it.isNotBlank() } ?: emptyList()

    val totalRates = rates.size.toLong()
    val rateStatuses = rates.map { 
        when {
            it.endsWith(":P", ignoreCase = true) -> 1
            it.endsWith(":X", ignoreCase = true) -> 2
            else -> 0
        }
    }
    val numRatePagate = rateStatuses.count { it == 1 || it == 2 }.toLong()

    val importoPagato = rates.filter { it.endsWith(":P", ignoreCase = true) }.sumOf { 
        if (isTax) {
            it.split("@").firstOrNull()?.replace(Regex(":[PX]", RegexOption.IGNORE_CASE), "")?.toLongOrNull() ?: 0L
        } else {
            it.replace(Regex(":[PX]", RegexOption.IGNORE_CASE), "").toLongOrNull() ?: 0L
        }
    }

    val totaleCartella = rates.sumOf { 
        if (isTax) {
            it.split("@").firstOrNull()?.replace(Regex(":[PX]", RegexOption.IGNORE_CASE), "")?.toLongOrNull() ?: 0L
        } else {
            it.replace(Regex(":[PX]", RegexOption.IGNORE_CASE), "").toLongOrNull() ?: 0L
        }
    }
    val progress = if (totaleCartella == 0L) 1f else (importoPagato.toFloat() / totaleCartella.toFloat()).coerceIn(0f, 1f)

    Column(modifier = Modifier.fillMaxWidth().clickable { onOpenDetails(cartella) }.padding(bottom = 16.dp)) {
        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
            Text(
                nomeCartella,
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                modifier = Modifier.weight(1f),
                color = if (isArchived) Color(0xFFA0A0A0) else Color.White
            )

            Row(verticalAlignment = Alignment.CenterVertically) {
                if (!isArchived) {
                    IconButton(onClick = { onEstinguiCartella(cartella) }, modifier = Modifier.size(28.dp)) {
                        Icon(Icons.Outlined.CheckCircle, "Estingui Anticipatamente", tint = Color(0xFF10B981), modifier = Modifier.size(18.dp))
                    }
                    Spacer(Modifier.width(4.dp))

                    IconButton(onClick = { onEditCartella(cartella) }, modifier = Modifier.size(28.dp)) {
                        Icon(Icons.Outlined.Edit, "Modifica", tint = Color(0xFF60A5FA), modifier = Modifier.size(18.dp))
                    }
                    Spacer(Modifier.width(4.dp))
                } else {
                    Text("SALDATO", style = MaterialTheme.typography.labelSmall, color = Color(0xFF10B981), fontWeight = FontWeight.Black)
                    Spacer(Modifier.width(8.dp))
                }

                IconButton(onClick = { onDeleteCartella(cartella) }, modifier = Modifier.size(28.dp)) {
                    Icon(Icons.Outlined.Delete, "Elimina", tint = MaterialTheme.colorScheme.error.copy(alpha = if (isArchived) 0.5f else 1f), modifier = Modifier.size(18.dp))
                }
            }
        }

        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.Bottom) {
            Text("${numRatePagate}/${totalRates} rate completate", style = MaterialTheme.typography.labelSmall, color = Color(0xFFA0A0A0))
            Text("${(progress * 100).toInt()}%", style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.ExtraBold), color = if (isArchived) Color(0xFF10B981) else MaterialTheme.colorScheme.error)
        }

        Spacer(Modifier.height(8.dp))
        Row(horizontalArrangement = Arrangement.spacedBy(4.dp), verticalAlignment = Alignment.CenterVertically) {
            rateStatuses.forEach { statusInt ->
                Icon(
                    imageVector = when (statusInt) { 1 -> Icons.Rounded.CheckCircle; 2 -> Icons.Default.Close; else -> Icons.Rounded.RadioButtonUnchecked },
                    contentDescription = null,
                    tint = when (statusInt) { 1 -> Color(0xFF10B981); 2 -> MaterialTheme.colorScheme.error; else -> MaterialTheme.colorScheme.outline.copy(alpha = 0.5f) },
                    modifier = Modifier.size(18.dp)
                )
            }
        }
        Spacer(Modifier.height(8.dp))
        LinearProgressIndicator(
            progress = { progress },
            modifier = Modifier.fillMaxWidth().height(8.dp).clip(CircleShape),
            color = if (isArchived) Color(0xFF10B981) else MaterialTheme.colorScheme.error,
            trackColor = (if (isArchived) Color(0xFF10B981) else MaterialTheme.colorScheme.error).copy(alpha = 0.2f)
        )
        Spacer(Modifier.height(4.dp))

        Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween) {
            Row(verticalAlignment = Alignment.Bottom) {
                Text("Pagato: ", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.onSurfaceVariant, modifier = Modifier.padding(bottom = 1.dp))
                ModernMoneyText(cents = importoPagato, hideBalances = hideBalances, color = MaterialTheme.colorScheme.onSurfaceVariant, integerFontSize = 12.sp, decimalFontSize = 10.sp, symbolFontSize = 10.sp)
            }
            Row(verticalAlignment = Alignment.Bottom) {
                Text("Totale: ", style = MaterialTheme.typography.labelSmall, color = MaterialTheme.colorScheme.onSurfaceVariant, modifier = Modifier.padding(bottom = 1.dp))
                ModernMoneyText(cents = if(isArchived) importoPagato else totaleCartella, hideBalances = hideBalances, color = MaterialTheme.colorScheme.onSurfaceVariant, integerFontSize = 12.sp, decimalFontSize = 10.sp, symbolFontSize = 10.sp)
            }
        }
    }
}@Composable
fun CartellaWizardDialog(
    activeAccountId: String?,
    accounts: List<AccountEntity> = emptyList(),
    onDismiss: () -> Unit,
    onSaveRecurring: (RecurringExpenseEntity) -> Unit,
    onSaveTransaction: ((TransactionEntity) -> Unit)? = null,
    editingEntity: RecurringExpenseEntity? = null,
    payPeriodEnd: LocalDate? = null
) {
    val isEditing = editingEntity != null
    val isTax = false

    val resolvedAccountId = if (editingEntity?.accountId != null && editingEntity.accountId != "NEW_TAX_MODE" && editingEntity.accountId!!.isNotBlank()) {
        editingEntity.accountId!!
    } else if (activeAccountId != null && activeAccountId != "NEW_TAX_MODE" && activeAccountId.isNotBlank()) {
        activeAccountId
    } else {
        accounts.firstOrNull { !it.isDeleted && !it.isArchived && !it.id.startsWith("sys_") }?.id
            ?: accounts.firstOrNull { !it.isDeleted }?.id
            ?: ""
    }

    var name by remember { mutableStateOf(editingEntity?.name ?: if (isTax) "Tassa Comunale (Es. IMU)" else "Cartella Agenzia Entrate") }

    val existingRates = editingEntity?.notes?.let { note ->
        if (isTax) {
            Regex("\\[TAX:([^\\]]*)\\]", RegexOption.IGNORE_CASE).find(note)?.groupValues?.get(1)?.split(",")?.map { it.trim() }?.filter { it.isNotEmpty() }
        } else {
            Regex("\\[CART:([^\\]]*)\\]", RegexOption.IGNORE_CASE).find(note)?.groupValues?.get(1)?.split(",")?.map { it.trim() }?.filter { it.isNotEmpty() }
        }
    } ?: emptyList()
    var numRateText by remember { mutableStateOf(if (isEditing) existingRates.size.toString() else if (isTax) "2" else "4") }

    var rateAmounts by remember {
        mutableStateOf(
            if (isEditing) {
                existingRates.map {
                    val baseAmt = if (isTax) it.split("@").firstOrNull() ?: "0" else it
                    val vCents = baseAmt.replace(Regex(":[PX]", RegexOption.IGNORE_CASE), "").toLongOrNull() ?: 0L
                    val vEuros = vCents / 100.0
                    if (vEuros % 1.0 == 0.0) vEuros.toLong().toString() else vEuros.toString()
                }.toMutableList()
            } else List(if (isTax) 2 else 4) { "" }.toMutableList()
        )
    }

    var rateMonths by remember {
        mutableStateOf(
            if (isEditing && isTax) {
                existingRates.map {
                    val m = it.split("@").getOrNull(1)?.replace(Regex(":[PX]", RegexOption.IGNORE_CASE), "")?.toIntOrNull() ?: 6
                    m.toString()
                }.toMutableList()
            } else List(if (isTax) 2 else 4) { "6" }.toMutableList()
        )
    }
    // 0 = Pending (Circle), 1 = Paid (Green Check :P), 2 = Skipped (Red X :X)
    var ratePaidStatus by remember {
        mutableStateOf(
            if (isEditing) {
                existingRates.map {
                    when {
                        it.endsWith(":P", ignoreCase = true) -> 1
                        it.endsWith(":X", ignoreCase = true) -> 2
                        else -> 0
                    }
                }.toMutableList()
            } else List(if (isTax) 2 else 4) { 0 }.toMutableList()
        )
    }
    var actionDialogIndex by remember { mutableStateOf<Int?>(null) }

    val today = Clock.System.todayIn(TimeZone.currentSystemDefault())
    var startMonth by remember { mutableStateOf(editingEntity?.let { LocalDate.fromEpochDays(it.startEpochDay.toInt()) } ?: today) }
    var dayInput by remember(startMonth.dayOfMonth) { mutableStateOf(startMonth.dayOfMonth.toString()) }

    LaunchedEffect(startMonth, dayInput, numRateText) {
        if (!isEditing) {
            val num = numRateText.toIntOrNull()?.coerceIn(1, 120) ?: return@LaunchedEffect
            val d = dayInput.toIntOrNull() ?: startMonth.dayOfMonth
            val calToday = Clock.System.todayIn(TimeZone.currentSystemDefault())
            val todayEpoch = calToday.toEpochDays()
            
            val newStatusList = ratePaidStatus.toMutableList()
            while (newStatusList.size < num) newStatusList.add(0)
            while (newStatusList.size > num) newStatusList.removeAt(newStatusList.size - 1)
            
            // Auto-check solo se rigorosamente nel passato (esclude OGGI)
            for (i in newStatusList.indices) {
                var tm = startMonth.monthNumber + i
                var ty = startMonth.year
                while (tm > 12) { tm -= 12; ty++ }
                val maxD = when (tm) { 2 -> if (ty % 4 == 0 && (ty % 100 != 0 || ty % 400 == 0)) 29 else 28; 4, 6, 9, 11 -> 30; else -> 31 }
                val targetDay = d.coerceAtMost(maxD)
                val epoch = LocalDate(ty, tm, targetDay).toEpochDays()
                
                newStatusList[i] = if (epoch < todayEpoch) 1 else 0
            }
            ratePaidStatus = newStatusList
        }
    }

    LaunchedEffect(numRateText) {
        val num = numRateText.toIntOrNull()?.coerceIn(1, 120) ?: return@LaunchedEffect
        if (num != rateAmounts.size) {
            val newList = rateAmounts.toMutableList()
            val newStatusList = ratePaidStatus.toMutableList()
            while (newList.size < num) {
                newList.add("")
                newStatusList.add(0)
            }
            while (newList.size > num) {
                newList.removeAt(newList.size - 1)
                newStatusList.removeAt(newStatusList.size - 1)
            }
            rateAmounts = newList
            ratePaidStatus = newStatusList
        }
    }

    actionDialogIndex?.let { index ->
        var targetMonth = startMonth.monthNumber + index
        var targetYear = startMonth.year
        while (targetMonth > 12) { targetMonth -= 12; targetYear++ }

        AlertDialog(
            onDismissRequest = { actionDialogIndex = null },
            shape = RoundedCornerShape(24.dp),
            containerColor = Color(0xFF1E1B4B),
            title = {
                Text("Rata di $targetMonth/$targetYear", style = MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold), color = Color.White)
            },
            text = {
                Text("Vuoi scalare i soldi dal saldo (creando un movimento di spesa) o vuoi solo segnarla come pagata?", style = MaterialTheme.typography.bodyMedium, color = Color(0xFFA0A0A0))
            },
            confirmButton = {
                Column(modifier = Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    Button(
                        onClick = {
                            val mutStatus = ratePaidStatus.toMutableList()
                            mutStatus[index] = 1
                            ratePaidStatus = mutStatus

                            val nowMs = Clock.System.now().toEpochMilliseconds()
                            val cartellaId = editingEntity?.id ?: "cart_$nowMs"
                            val amtCents = (rateAmounts.getOrNull(index)?.replace(",", ".")?.toDoubleOrNull()?.times(100))?.toLong() ?: 0L

                            if (amtCents > 0L) {
                                val tx = TransactionEntity(
                                    id = "tx_${nowMs}_${Random.nextInt(1000)}",
                                    accountId = resolvedAccountId,
                                    toAccountId = null,
                                    fromAccountId = null,
                                    amountCents = amtCents,
                                    type = "EXPENSE",
                                    dateEpochDay = Clock.System.todayIn(TimeZone.currentSystemDefault()).toEpochDays().toLong(),
                                    categoryId = editingEntity?.categoryId ?: "default",
                                    description = "$name - Rata ${index + 1}",
                                    merchantKey = "[rec:$cartellaId] [manual]",
                                    subcategoryText = "Cartella Esattoriale",
                                    logoEmoji = "🏛️",
                                    logoUri = null,
                                    isHidden = false,
                                    isDeleted = false,
                                    reminderDays = -1L,
                                    createdAtEpochMs = nowMs,
                                    updatedAtEpochMs = nowMs
                                )
                                onSaveTransaction?.invoke(tx)
                            }
                            actionDialogIndex = null
                        },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF10B981), contentColor = Color.White)
                    ) {
                        Text("SCALA I SOLDI E SEGNA PAGATA", fontWeight = FontWeight.Bold)
                    }

                    Button(
                        onClick = {
                            val mutStatus = ratePaidStatus.toMutableList()
                            mutStatus[index] = 1
                            ratePaidStatus = mutStatus
                            actionDialogIndex = null
                        },
                        modifier = Modifier.fillMaxWidth(),
                        shape = RoundedCornerShape(12.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = Color.White.copy(alpha = 0.15f), contentColor = Color.White)
                    ) {
                        Text("SOLO SEGNA PAGATA", fontWeight = FontWeight.Bold)
                    }

                    TextButton(
                        onClick = { actionDialogIndex = null },
                        modifier = Modifier.fillMaxWidth()
                    ) {
                        Text("ANNULLA", fontWeight = FontWeight.Bold, color = Color.White.copy(alpha = 0.6f))
                    }
                }
            },
            dismissButton = null
        )
    }

    Dialog(
        onDismissRequest = onDismiss,
        properties = DialogProperties(usePlatformDefaultWidth = false)
    ) {
        Surface(
            shape = RoundedCornerShape(28.dp),
            color = Color.White.copy(alpha = 0.12f),
            border = BorderStroke(1.5.dp, Color.White.copy(alpha = 0.40f)),
            shadowElevation = 24.dp,
            modifier = Modifier.fillMaxWidth(0.95f).fillMaxHeight(0.85f).imePadding()
        ) {
            Column(modifier = Modifier.fillMaxSize().padding(24.dp).verticalScroll(rememberScrollState())) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Icon(Icons.Rounded.AccountBalance, null, tint = MaterialTheme.colorScheme.error)
                    Spacer(Modifier.width(8.dp))
                    Text(if (isEditing) "Modifica Cartella" else "Nuova Cartella a Rate", style = MaterialTheme.typography.titleLarge, fontWeight = FontWeight.Black, color = Color.White)
                }
                Text("Tocca i cerchi vuoti per marcare o smarcare le rate pagate.", style = MaterialTheme.typography.bodySmall, color = Color(0xFFA0A0A0))

                Spacer(Modifier.height(16.dp))

                OutlinedTextField(
                    value = name, onValueChange = { name = it },
                    label = { Text("Nome Cartella/Debito", color = Color(0xFFA0A0A0)) },
                    modifier = Modifier.fillMaxWidth(), singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                Spacer(Modifier.height(8.dp))

                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    OutlinedTextField(
                        value = numRateText, onValueChange = { numRateText = it },
                        label = { Text("N° Rate", color = Color(0xFFA0A0A0)) },
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                        modifier = Modifier.weight(0.4f), singleLine = true,
                        colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                    )

                    Column(modifier = Modifier.weight(0.6f)) {
                        Row(modifier = Modifier.fillMaxWidth().height(56.dp), verticalAlignment = Alignment.CenterVertically) {
                            IconButton(onClick = {
                                var m = startMonth.monthNumber - 1
                                var y = startMonth.year
                                if (m < 1) { m = 12; y-- }
                                startMonth = LocalDate(y, m, startMonth.dayOfMonth.coerceAtMost(28))
                            }) { Icon(Icons.Default.ChevronLeft, "Indietro", tint = Color(0xFF60A5FA)) }

                            Text(
                                text = "${startMonth.monthNumber.toString().padStart(2, '0')}/${startMonth.year}",
                                modifier = Modifier.weight(1f),
                                textAlign = androidx.compose.ui.text.style.TextAlign.Center,
                                fontWeight = FontWeight.Bold,
                                color = Color.White
                            )

                            IconButton(onClick = {
                                var m = startMonth.monthNumber + 1
                                var y = startMonth.year
                                if (m > 12) { m = 1; y++ }
                                startMonth = LocalDate(y, m, startMonth.dayOfMonth.coerceAtMost(28))
                            }) { Icon(Icons.Default.ChevronRight, "Avanti", tint = Color(0xFF60A5FA)) }
                        }
                    }
                }

                OutlinedTextField(
                    value = dayInput,
                    onValueChange = {
                        if (it.all { c -> c.isDigit() }) {
                            dayInput = it
                            val d = it.toIntOrNull()
                            if (d != null && d in 1..31) {
                                startMonth = LocalDate(startMonth.year, startMonth.monthNumber, d)
                            }
                        }
                    },
                    label = { Text("Giorno di addebito (1-31)", color = Color(0xFFA0A0A0)) },
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                    modifier = Modifier.fillMaxWidth(),
                    singleLine = true,
                    colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                )

                Spacer(Modifier.height(16.dp))
                HorizontalDivider(color = Color.White.copy(alpha = 0.15f))
                Spacer(Modifier.height(8.dp))

                Column {
                    rateAmounts.forEachIndexed { index, _ ->
                        var targetMonth = startMonth.monthNumber + index
                        var targetYear = startMonth.year
                        while (targetMonth > 12) { targetMonth -= 12; targetYear++ }

                        val statusInt = ratePaidStatus.getOrElse(index) { 0 }

                        Row(
                            verticalAlignment = Alignment.CenterVertically,
                            modifier = Modifier.fillMaxWidth().padding(vertical = 4.dp)
                        ) {
                            Icon(
                                imageVector = when (statusInt) { 1 -> Icons.Rounded.CheckCircle; 2 -> Icons.Default.Close; else -> Icons.Rounded.RadioButtonUnchecked },
                                contentDescription = null,
                                tint = when (statusInt) { 1 -> Color(0xFF10B981); 2 -> MaterialTheme.colorScheme.error; else -> Color.White.copy(alpha = 0.3f) },
                                modifier = Modifier
                                    .size(32.dp)
                                    .padding(end = 6.dp)
                                    .clip(CircleShape)
                                    .clickable {
                                        val mutStatus = ratePaidStatus.toMutableList()
                                        if (statusInt == 2) {
                                            actionDialogIndex = index
                                        } else if (statusInt == 0) {
                                            mutStatus[index] = 1
                                            ratePaidStatus = mutStatus
                                        } else if (statusInt == 1) {
                                            val todayE = Clock.System.todayIn(TimeZone.currentSystemDefault()).toEpochDays()
                                            val maxD = when (targetMonth) { 2 -> if (targetYear % 4 == 0 && (targetYear % 100 != 0 || targetYear % 400 == 0)) 29 else 28; 4, 6, 9, 11 -> 30; else -> 31 }
                                            val epoch = LocalDate(targetYear, targetMonth, startMonth.dayOfMonth.coerceAtMost(maxD)).toEpochDays()
                                            mutStatus[index] = if (epoch < todayE) 2 else 0
                                            ratePaidStatus = mutStatus
                                        }
                                    }
                            )
                            Text("Rata ${index + 1} ($targetMonth/$targetYear)", modifier = Modifier.weight(1f), style = MaterialTheme.typography.bodyMedium, fontWeight = FontWeight.Bold, color = Color.White)
                            OutlinedTextField(
                                value = rateAmounts[index],
                                onValueChange = { newVal ->
                                    if (newVal.all { it.isDigit() || it == ',' || it == '.' }) {
                                        val mutList = rateAmounts.toMutableList()
                                        mutList[index] = newVal
                                        rateAmounts = mutList
                                    }
                                },
                                label = { Text("€", color = Color(0xFFA0A0A0)) },
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
                                modifier = Modifier.weight(1f), singleLine = true,
                                colors = OutlinedTextFieldDefaults.colors(focusedTextColor = Color.White, unfocusedTextColor = Color.White, focusedContainerColor = Color.Black.copy(alpha = 0.35f), unfocusedContainerColor = Color.Black.copy(alpha = 0.25f), focusedBorderColor = Color(0xFF60A5FA), unfocusedBorderColor = Color.White.copy(alpha = 0.20f))
                            )
                        }
                    }
                }

                val totalSum = rateAmounts.sumOf { (it.replace(",", ".").toDoubleOrNull() ?: 0.0) }

                Spacer(Modifier.height(8.dp))
                val totalSumCents = (totalSum * 100).toLong()
                Row(verticalAlignment = Alignment.Bottom) {
                    Text("Totale: ", style = MaterialTheme.typography.titleMedium, color = MaterialTheme.colorScheme.error, fontWeight = FontWeight.Black, modifier = Modifier.padding(bottom = 1.dp))
                    ModernMoneyText(cents = totalSumCents, hideBalances = false, color = MaterialTheme.colorScheme.error, integerFontSize = 20.sp, decimalFontSize = 14.sp, symbolFontSize = 14.sp)
                }
                Spacer(Modifier.height(16.dp))

                Row(modifier = Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                    TextButton(onClick = onDismiss) { Text("ANNULLA", color = Color.White, fontWeight = FontWeight.Bold) }
                    Spacer(Modifier.width(12.dp))
                    Button(
                        onClick = {
                            val nowMs = Clock.System.now().toEpochMilliseconds()
                            val cartellaId = editingEntity?.id ?: "cart_${nowMs}"

                            val rateCents = rateAmounts.mapNotNull { (it.replace(",", ".").toDoubleOrNull()?.times(100))?.toLong() }
                            if (rateCents.isEmpty()) return@Button

                            val todayObj = Clock.System.todayIn(TimeZone.currentSystemDefault())
                            val todayEpoch = todayObj.toEpochDays().toLong()
                            val maxDaysInMonth = when (todayObj.monthNumber) { 2 -> if (todayObj.year % 4 == 0 && (todayObj.year % 100 != 0 || todayObj.year % 400 == 0)) 29 else 28; 4, 6, 9, 11 -> 30; else -> 31 }
                            val endOfPeriodEpoch = (payPeriodEnd ?: LocalDate(todayObj.year, todayObj.monthNumber, maxDaysInMonth)).toEpochDays().toLong()

                            val newRatesList = mutableListOf<String>()
                            var firstUnpaidAmount = -1L
                            var resolvedCount = 0

                            val finalStatusList = ratePaidStatus.toMutableList()

                            for (i in rateCents.indices) {
                                var targetMonth = startMonth.monthNumber + i
                                var targetYear = startMonth.year
                                while (targetMonth > 12) { targetMonth -= 12; targetYear++ }
                                val maxD = when (targetMonth) { 2 -> if (targetYear % 4 == 0 && (targetYear % 100 != 0 || targetYear % 400 == 0)) 29 else 28; 4, 6, 9, 11 -> 30; else -> 31 }
                                val rateEpochDay = LocalDate(targetYear, targetMonth, startMonth.dayOfMonth.coerceAtMost(maxD)).toEpochDays().toLong()

                                var statusInt = finalStatusList.getOrElse(i) { 0 }

                                if (!isEditing && statusInt == 0) {
                                    if (rateEpochDay < todayEpoch) {
                                        // Passato (retroattiva): Segnamo come pagata (:P) con spunta verde, NESSUN movimento in uscita!
                                        statusInt = 1
                                        finalStatusList[i] = 1
                                    } else if (rateEpochDay == todayEpoch) {
                                        // Oggi (giorno di inserimento): Segnamo come pagata (:P) e creiamo subito il movimento oggi!
                                        statusInt = 1
                                        finalStatusList[i] = 1

                                        if (rateCents[i] > 0L) {
                                            val subcatLabel = if (isTax) "Tassa Comunale" else "Cartella Esattoriale"
                                            val tx = TransactionEntity(
                                                id = "tx_${nowMs}_${i}_${Random.nextInt(1000)}",
                                                accountId = resolvedAccountId,
                                                toAccountId = null,
                                                fromAccountId = null,
                                                amountCents = rateCents[i],
                                                type = "EXPENSE",
                                                dateEpochDay = todayEpoch,
                                                categoryId = editingEntity?.categoryId ?: "default",
                                                description = "$name - Rata ${i + 1}",
                                                merchantKey = "[rec:$cartellaId] [manual]",
                                                subcategoryText = subcatLabel,
                                                logoEmoji = "🏛️",
                                                logoUri = null,
                                                isHidden = false,
                                                isDeleted = false,
                                                reminderDays = -1L,
                                                createdAtEpochMs = nowMs + i,
                                                updatedAtEpochMs = nowMs + i
                                            )
                                            onSaveTransaction?.invoke(tx)
                                        }
                                    }
                                    // Se rateEpochDay > todayEpoch: rimane 0 (programmata), scalando dal saldo disponibile e creando il movimento il giorno programmato.
                                }

                                val isResolved = (statusInt == 1 || statusInt == 2)
                                if (isResolved) resolvedCount++

                                val rateStr = when (statusInt) {
                                    1 -> "${rateCents[i]}:P"
                                    2 -> "${rateCents[i]}:X"
                                    else -> rateCents[i].toString()
                                }
                                newRatesList.add(rateStr)

                                if (statusInt == 0 && firstUnpaidAmount == -1L) {
                                    firstUnpaidAmount = rateCents[i]
                                }
                            }

                            var nextM = startMonth.monthNumber + resolvedCount
                            var nextY = startMonth.year
                            while(nextM > 12) { nextM -= 12; nextY++ }
                            val maxD = when(nextM) { 2 -> if (nextY%4==0 && (nextY%100!=0 || nextY%400==0)) 29 else 28; 4,6,9,11 -> 30; else -> 31 }
                            val infallibileNextEpochDay = LocalDate(nextY, nextM, startMonth.dayOfMonth.coerceAtMost(maxD)).toEpochDays().toLong()

                            val finalAmount = if (firstUnpaidAmount != -1L) firstUnpaidAmount else 0L
                            val finalNotesCart = newRatesList.joinToString(",")
                            val isArchivedNow = firstUnpaidAmount == -1L

                            val notesTag = if (isTax) "[TAX:$finalNotesCart]" else "[CART:$finalNotesCart]"
                            val notesStr = notesTag + (if (isArchivedNow || editingEntity?.notes?.contains("[ARCHIVED:true]") == true) " [ARCHIVED:true]" else "")

                            val newRec = RecurringExpenseEntity(
                                id = cartellaId,
                                name = name,
                                type = "EXPENSE",
                                amountCents = finalAmount,
                                accountId = resolvedAccountId,
                                toAccountId = null,
                                categoryId = editingEntity?.categoryId ?: "default",
                                dayOfMonth = startMonth.dayOfMonth.toLong(),
                                isEnabled = editingEntity?.isEnabled ?: !isArchivedNow,
                                isDeleted = false,
                                cycleTarget = rateCents.size.toLong(),
                                logoEmoji = editingEntity?.logoEmoji ?: "🏛️",
                                logoUri = editingEntity?.logoUri,
                                notes = notesStr,
                                autoGenerateOnReminder = true,
                                nextEpochDay = infallibileNextEpochDay,
                                frequency = editingEntity?.frequency ?: "Mensile",
                                isPaused = editingEntity?.isPaused ?: false,
                                startEpochDay = startMonth.toEpochDays().toLong(),
                                createdAtEpochMs = editingEntity?.createdAtEpochMs ?: nowMs,
                                updatedAtEpochMs = nowMs
                            )

                            onSaveRecurring(newRec)
                        },
                        shape = RoundedCornerShape(14.dp),
                        colors = ButtonDefaults.buttonColors(containerColor = MaterialTheme.colorScheme.error, contentColor = Color.White)
                    ) {
                        Text("SALVA PIANO", fontWeight = FontWeight.Bold)
                    }
                }
            }
        }
    }
}
